import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { RichMentionsContext, cn, Icon, Spinner, Text, Button, DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, RichMentionsInput, processMentionsInHTML as processMentionsInHTML$1, ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogCloseButton, RichMentionsProvider } from "@flatfile/baseline";
import React, { useContext, useRef, useState, useLayoutEffect, useMemo, useEffect, useCallback, isValidElement, Fragment as Fragment$1, Children, memo, Suspense } from "react";
import { createPortal } from "react-dom";
import { AreaChart, CartesianGrid, XAxis, Area, BarChart, Bar, PieChart, Pie, Cell, Tooltip, Legend } from "recharts";
import { marked } from "marked";
import ReactMarkdown from "react-markdown";
import { animate } from "framer-motion";
function MentionsPopover$1() {
  var _a;
  const { opened, index, results, setActiveItemIndex, selectItem } = useContext(RichMentionsContext);
  const popoverRef = useRef(null);
  const [popoverHeight, setPopoverHeight] = useState(0);
  const onHoverItem = (index2) => () => setActiveItemIndex(index2);
  useLayoutEffect(() => {
    if (popoverRef.current) {
      setPopoverHeight(popoverRef.current.offsetHeight);
    }
  }, [results.length]);
  if (!opened || !results.length) return null;
  const chatContainer = (_a = document.querySelector(
    "#chat-ui-content-container"
  )) == null ? void 0 : _a.parentElement;
  if (!chatContainer) return null;
  const containerRect = chatContainer.getBoundingClientRect();
  const popoverWidth = 240;
  let left = opened.x - containerRect.left + 14;
  let top = opened.y - containerRect.top - popoverHeight - 8;
  const maxLeft = containerRect.width - popoverWidth - 16;
  if (left > maxLeft) left = maxLeft;
  if (left < 16) left = 16;
  return createPortal(
    /* @__PURE__ */ jsx(
      "div",
      {
        ref: popoverRef,
        className: "absolute bg-base-white p-0 border border-gray-200 shadow-sm text-sm flex flex-col w-full max-w-[240px] max-h-[300px] overflow-y-auto rounded-md z-[99999] pointer-events-auto",
        style: { left: `${left}px`, top: `${top}px` },
        children: results.map((item, i) => {
          const displayText = item.displayText || "";
          return /* @__PURE__ */ jsxs(
            "button",
            {
              type: "button",
              className: cn(
                "w-full p-2 cursor-pointer hover:bg-gray-100 rounded-md flex items-center justify-between",
                i === index && "bg-gray-100"
              ),
              onClick: () => selectItem(item),
              onMouseOver: onHoverItem(i),
              children: [
                /* @__PURE__ */ jsxs("span", { className: "flex items-center min-w-0 flex-shrink overflow-hidden mr-1.5", children: [
                  /* @__PURE__ */ jsx("span", { className: "truncate max-w-[120px]", children: displayText }),
                  item.type && /* @__PURE__ */ jsx("span", { className: "text-gray-500 text-xs ml-1.5 text-[11px] whitespace-nowrap flex-shrink-0", children: item.type })
                ] }),
                item.groupName && /* @__PURE__ */ jsxs("span", { className: "text-gray-400 text-[11px] flex items-center mr-1 min-w-0 flex-shrink overflow-hidden", children: [
                  /* @__PURE__ */ jsx(
                    Icon,
                    {
                      name: "table",
                      className: "text-gray-400 mr-1 flex-shrink-0",
                      size: 12
                    }
                  ),
                  /* @__PURE__ */ jsx("span", { className: "truncate max-w-[80px]", children: item.groupName })
                ] })
              ]
            },
            item.ref
          );
        })
      }
    ),
    chatContainer
  );
}
const ChatEmptyState = ({
  title = "Flatfile Agent",
  description = "The Flatfile Agent is a helpful assistant that can help you with your data processing tasks. You can ask it to do anything you ask Ai Transform to do.",
  icon,
  options = [],
  isLoadingChat = false
}) => {
  if (isLoadingChat) {
    return /* @__PURE__ */ jsx("div", { className: "h-full flex flex-col items-center justify-center gap-4 text-center p-4", children: /* @__PURE__ */ jsx(Spinner, { size: 32, variant: "primary", className: "mb-2" }) });
  }
  return /* @__PURE__ */ jsxs("div", { className: "h-full flex flex-col items-center justify-center gap-4 text-center p-4", children: [
    icon || /* @__PURE__ */ jsx(Icon, { name: "blur", className: "!h-[100px] !w-[100px] !text-[100px]" }),
    /* @__PURE__ */ jsx(Text, { variant: "title3", emphasized: true, children: title }),
    /* @__PURE__ */ jsx(Text, { variant: "subheadline", className: "max-w-[400px] text-muted", children: description }),
    options.length > 0 && /* @__PURE__ */ jsx("div", { className: "flex flex-wrap gap-2 mt-2 justify-center", children: options.map((option, index) => /* @__PURE__ */ jsx(
      Button,
      {
        variant: option.variant || "secondary",
        onClick: option.onClick,
        size: "sm",
        className: "theme-static",
        children: option.title
      },
      index
    )) })
  ] });
};
const ChatHeader = ({
  title = "Flatfile Agent",
  iconName = "blur",
  actions,
  onClose,
  onExpand,
  onCollapse,
  isExpanded,
  isCollapsed,
  allowClose = true,
  allowExpand = true,
  allowCollapse = false,
  className
}) => {
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "flex items-center h-12 justify-between px-4 py-2 flex-shrink-0",
        className
      ),
      children: [
        /* @__PURE__ */ jsx("div", { className: "flex items-center gap-4", children: /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsx(Icon, { name: iconName }),
          /* @__PURE__ */ jsx(Text, { emphasized: true, children: title })
        ] }) }),
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
          actions,
          allowCollapse && /* @__PURE__ */ jsx(
            Button,
            {
              variant: "ghost",
              size: "icon-md",
              onClick: onCollapse,
              icon: "sidebar-left-arrow",
              className: cn(
                "transition-[max-width,opacity] duration-300 ease-in-out overflow-hidden",
                isCollapsed ? "max-w-0 opacity-0" : "max-w-[36px] opacity-100",
                isCollapsed ? "rotate-180" : "",
                "theme-static"
              )
            }
          ),
          allowExpand && onExpand && /* @__PURE__ */ jsx(
            Button,
            {
              variant: "ghost",
              size: "icon-md",
              onClick: onExpand,
              icon: isExpanded ? "minimize" : "expand-simple",
              className: cn(isExpanded ? "rotate-90" : "", "theme-static")
            }
          ),
          allowClose && onClose && /* @__PURE__ */ jsx(
            Button,
            {
              variant: "ghost",
              size: "icon-md",
              onClick: onClose,
              icon: "cross-small",
              className: "theme-static"
            }
          )
        ] })
      ]
    }
  );
};
const MODELS = {
  CLAUDE_3_HAIKU: "anthropic.claude-3-haiku-20240307-v1:0",
  CLAUDE_3_SONNET: "anthropic.claude-3-sonnet-20240229-v1:0",
  CLAUDE_3_5_HAIKU: "anthropic.claude-3-5-haiku-20241022-v1:0",
  CLAUDE_3_5_SONNET_V1: "anthropic.claude-3-5-sonnet-20240620-v1:0",
  CLAUDE_3_5_SONNET_V2: "anthropic.claude-3-5-sonnet-20241022-v2:0",
  CLAUDE_3_7_SONNET: "us.anthropic.claude-3-7-sonnet-20250219-v1:0",
  CLAUDE_SONNET_4: "us.anthropic.claude-sonnet-4-20250514-v1:0",
  CLAUDE_OPUS_4: "us.anthropic.claude-opus-4-20250514-v1:0"
};
const modelNames = {
  CLAUDE_3_HAIKU: "Claude 3 Haiku",
  CLAUDE_3_SONNET: "Claude 3 Sonnet",
  CLAUDE_3_5_HAIKU: "Claude 3.5 Haiku",
  CLAUDE_3_5_SONNET_V1: "Claude 3.5 Sonnet v1",
  CLAUDE_3_5_SONNET_V2: "Claude Sonnet 3.5 v2",
  CLAUDE_3_7_SONNET: "Claude Sonnet 3.7",
  CLAUDE_SONNET_4: "Claude Sonnet 4",
  CLAUDE_OPUS_4: "Claude Opus 4"
};
const SelectModelDropdown = ({
  model,
  disabled,
  onModelChange
}) => {
  return /* @__PURE__ */ jsxs(DropdownMenu, { children: [
    /* @__PURE__ */ jsx(DropdownMenuTrigger, { children: /* @__PURE__ */ jsxs(
      Button,
      {
        size: "sm",
        variant: "ghost",
        disabled,
        type: "button",
        className: "px-0 mr-2 flex items-center gap-1 theme-static text-xs focus:outline-none",
        id: "chat-input-mention-button",
        children: [
          /* @__PURE__ */ jsx("span", { className: "text-xs", children: modelNames[model] }),
          /* @__PURE__ */ jsx("span", { className: "text-sm text-muted-foreground -mt-2", children: "⌄" })
        ]
      }
    ) }),
    /* @__PURE__ */ jsx(DropdownMenuContent, { align: "start", children: Object.entries(MODELS).map(([key]) => /* @__PURE__ */ jsxs(
      DropdownMenuItem,
      {
        onClick: () => onModelChange(key),
        children: [
          modelNames[key],
          model === key && /* @__PURE__ */ jsx("span", { className: "ml-2 text-xs text-muted-foreground", children: "✓" })
        ]
      },
      key
    )) })
  ] });
};
const ChatInputPrimitive = ({
  value,
  onSubmit,
  onChange,
  disabled = false,
  placeholder = "How can I help you?",
  isLoading = false,
  isGenerating = false,
  onStop,
  enableFileUpload = false,
  acceptedFileTypes,
  maxFileSize,
  model,
  onModelChange,
  autoFocus,
  hasMentions
}) => {
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [files, setFiles] = useState(void 0);
  const mentionsContext = useContext(RichMentionsContext);
  const isSendDisabled = disabled || isLoading || (!value || value.trim() === "") && (!files || files.length === 0);
  const fileInputAccept = useMemo(() => {
    if (!acceptedFileTypes) return void 0;
    return Object.entries(acceptedFileTypes).map(([_type, extensions]) => extensions.join(",")).join(",");
  }, [acceptedFileTypes]);
  useEffect(() => {
    if (!enableFileUpload) return;
    const handleGlobalDragLeave = (e) => {
      if (e.clientX <= 0 || e.clientY <= 0 || e.clientX >= window.innerWidth || e.clientY >= window.innerHeight) {
        setIsDragging(false);
      }
    };
    const handleGlobalDragEnd = () => {
      setIsDragging(false);
    };
    document.addEventListener("dragleave", handleGlobalDragLeave);
    document.addEventListener("dragend", handleGlobalDragEnd);
    return () => {
      document.removeEventListener("dragleave", handleGlobalDragLeave);
      document.removeEventListener("dragend", handleGlobalDragEnd);
    };
  }, [enableFileUpload]);
  const processFiles = useCallback(
    (inputFiles) => {
      let processedFiles = inputFiles;
      if (acceptedFileTypes) {
        const allowedExtensions = Object.values(acceptedFileTypes).flat();
        processedFiles = processedFiles.filter((file) => {
          const fileNameParts = file.name.split(".");
          if (fileNameParts.length <= 1) return false;
          const extension = "." + fileNameParts.pop().toLowerCase();
          return allowedExtensions.includes(extension);
        });
      }
      return maxFileSize ? processedFiles.filter((file) => file.size <= maxFileSize) : processedFiles;
    },
    [acceptedFileTypes, maxFileSize]
  );
  const updateFiles = useCallback(
    (newFiles) => {
      if (newFiles.length > 0) {
        const dataTransfer = new DataTransfer();
        if (files) {
          Array.from(files).forEach((file) => dataTransfer.items.add(file));
        }
        newFiles.forEach((file) => dataTransfer.items.add(file));
        setFiles(dataTransfer.files);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    },
    [files]
  );
  const handleFormSubmit = (e) => {
    e == null ? void 0 : e.preventDefault();
    if (isGenerating && onStop) {
      onStop();
      return;
    }
    let finalValue = value;
    if (mentionsContext == null ? void 0 : mentionsContext.inputElement) {
      const htmlContent = mentionsContext.inputElement.innerHTML;
      finalValue = processMentionsInHTML$1(htmlContent);
      onChange(finalValue);
    } else if (mentionsContext && typeof mentionsContext.getTransformedValue === "function") {
      finalValue = mentionsContext.getTransformedValue();
      onChange(finalValue);
    }
    onSubmit(e, {
      attachments: files
    });
    setFiles(void 0);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    if (mentionsContext == null ? void 0 : mentionsContext.inputElement) {
      const currentHeight = mentionsContext.inputElement.style.height;
      setTimeout(() => {
        const inputElement = mentionsContext.inputElement;
        if (inputElement) {
          const defaultHeight = "24px";
          if (currentHeight !== defaultHeight) {
            inputElement.style.transition = "height 300ms ease-in-out";
            inputElement.style.height = defaultHeight;
            setTimeout(() => {
              if (inputElement) {
                inputElement.style.transition = "";
              }
            }, 350);
          }
        }
      }, 400);
    }
  };
  const handleButtonClick = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (isGenerating && onStop) {
      onStop();
    } else {
      handleFormSubmit(e);
    }
  };
  const handleKeyDown = (e) => {
    if (!e.repeat && e.key === "Enter" && !e.shiftKey && !mentionsContext.opened) {
      e.preventDefault();
      if (!isGenerating) {
        handleFormSubmit();
      }
    }
  };
  const adjustInputHeight = (element) => {
    const currentHeight = element.style.height;
    element.style.transition = "";
    element.style.height = "24px";
    const newHeight = `${Math.min(element.scrollHeight, 200)}px`;
    if (currentHeight && Math.abs(parseInt(currentHeight) - parseInt(newHeight)) > 10) {
      element.style.transition = "height 150ms ease-out";
      element.style.height = newHeight;
      setTimeout(() => {
        element.style.transition = "";
      }, 200);
    } else {
      element.style.height = newHeight;
    }
  };
  const handleInputChange = (event) => {
    if (!event.currentTarget) return;
    const rawHtml = event.currentTarget.innerHTML;
    let finalValue = "";
    if (mentionsContext && typeof mentionsContext.getTransformedValue === "function") {
      finalValue = processMentionsInHTML$1(rawHtml);
    } else {
      finalValue = event.currentTarget.innerText;
    }
    const element = document.createElement("div");
    element.innerHTML = rawHtml;
    const isEffectivelyEmpty = element.innerText.trim() === "";
    if (isEffectivelyEmpty) {
      finalValue = "";
      if (event.currentTarget.innerHTML !== "") {
        event.currentTarget.innerHTML = "";
      }
    }
    onChange(finalValue);
    adjustInputHeight(event.currentTarget);
  };
  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const inputFiles = Array.from(e.target.files);
      const validFiles = processFiles(inputFiles);
      updateFiles(validFiles);
      e.target.value = "";
    }
  };
  const handleFileButtonClick = () => {
    var _a;
    (_a = fileInputRef.current) == null ? void 0 : _a.click();
  };
  const handleMentionButtonClick = () => {
    if (!mentionsContext.inputElement) return;
    mentionsContext.inputElement.focus();
    if (mentionsContext.opened) {
      if (mentionsContext.closeAutocomplete) {
        mentionsContext.closeAutocomplete();
      }
      const inputElement = mentionsContext.inputElement;
      const fragments = inputElement.querySelectorAll("[data-rich-mentions]");
      fragments.forEach((fragment2) => {
        var _a;
        const fragmentText = (_a = fragment2.textContent) == null ? void 0 : _a.trim();
        if (fragmentText === "@" || fragmentText === "") {
          fragment2.remove();
        }
      });
      return;
    }
    const selection = document.getSelection();
    if (!selection) return;
    const configs = mentionsContext.configs;
    if (!configs || configs.length === 0) return;
    const config = configs.find((cfg) => "@".match(cfg.query));
    if (!config) return;
    const { anchorNode } = selection;
    if (!anchorNode) return;
    const fragment = document.createElement("span");
    fragment.setAttribute("data-rich-mentions", "");
    fragment.setAttribute("spellcheck", "false");
    fragment.textContent = "@";
    if (config.customizeFragment) {
      config.customizeFragment(fragment, false);
    }
    const afterNode = document.createTextNode(" ");
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      if (!range.collapsed) {
        range.deleteContents();
      }
      range.insertNode(fragment);
      if (fragment.parentNode) {
        fragment.parentNode.insertBefore(afterNode, fragment.nextSibling);
      }
      const newRange = document.createRange();
      newRange.setStart(fragment.firstChild || fragment, 1);
      newRange.setEnd(fragment.firstChild || fragment, 1);
      selection.removeAllRanges();
      selection.addRange(newRange);
      if (mentionsContext.openAutocomplete) {
        mentionsContext.openAutocomplete(fragment, "@", config);
      }
      const inputEvent = new Event("input", { bubbles: true, cancelable: true });
      mentionsContext.inputElement.dispatchEvent(inputEvent);
      mentionsContext.inputElement.focus();
    }
  };
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);
  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  }, []);
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);
  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        const droppedFiles = Array.from(e.dataTransfer.files);
        const validFiles = processFiles(droppedFiles);
        if (validFiles.length > 0) {
          updateFiles(validFiles);
        }
      }
    },
    [processFiles, updateFiles]
  );
  const dragHandlers = enableFileUpload ? {
    onDragEnter: handleDragEnter,
    onDragLeave: handleDragLeave,
    onDragOver: handleDragOver,
    onDrop: handleDrop
  } : {};
  const fileList = files ? Array.from(files) : [];
  const inputHeightClass = "min-h-[24px]";
  const buttonIcon = isGenerating ? "stop-circle" : "arrow-up";
  const handleContainerClick = useCallback(
    (e) => {
      if (disabled || e.target instanceof HTMLButtonElement || e.target.closest("button")) {
        return;
      }
      if (mentionsContext == null ? void 0 : mentionsContext.inputElement) {
        mentionsContext.inputElement.focus();
      }
    },
    [disabled, mentionsContext]
  );
  return /* @__PURE__ */ jsx(
    "form",
    {
      onSubmit: handleFormSubmit,
      className: "focus:outline-none focus:ring-0",
      style: { outline: "none" },
      children: /* @__PURE__ */ jsxs(
        "div",
        {
          className: "px-2 py-2 bg-white relative transition-all duration-300 ease-in-out cursor-text",
          onClick: handleContainerClick,
          ...dragHandlers,
          children: [
            enableFileUpload && isDragging && /* @__PURE__ */ jsx("div", { className: "absolute inset-0 bg-primary-50 border-2 border-dashed border-primary-200 rounded-sm flex items-center justify-center z-10 theme-static", children: /* @__PURE__ */ jsx("div", { className: "text-primary-700 font-medium theme-static", children: "Drop files to upload" }) }),
            enableFileUpload && fileList.length > 0 && /* @__PURE__ */ jsx("div", { className: "flex flex-col w-full mb-0.5", children: fileList.map((file, index) => /* @__PURE__ */ jsxs(
              "div",
              {
                className: "inline-flex items-center px-2 py-1 rounded-sm border border-input text-primary-700 text-base bg-transparent w-full justify-between cursor-default theme-static",
                children: [
                  /* @__PURE__ */ jsx("span", { className: "truncate max-w-[150px]", children: file.name }),
                  /* @__PURE__ */ jsx(
                    "button",
                    {
                      type: "button",
                      className: "ml-1 text-primary hover:text-primary-700 focus:outline-none theme-static",
                      onClick: (e) => {
                        e.stopPropagation();
                        const newDataTransfer = new DataTransfer();
                        fileList.forEach((f, i) => {
                          if (i !== index) newDataTransfer.items.add(f);
                        });
                        setFiles(
                          newDataTransfer.files.length > 0 ? newDataTransfer.files : void 0
                        );
                      },
                      children: /* @__PURE__ */ jsx(Icon, { name: "cross-small", size: 12 })
                    }
                  )
                ]
              },
              `${file.name}-${index}`
            )) }),
            /* @__PURE__ */ jsxs(
              "div",
              {
                className: `flex flex-col ${fileList.length > 0 ? "mt-2" : ""} cursor-text`,
                children: [
                  /* @__PURE__ */ jsx(
                    "div",
                    {
                      className: "w-full focus:outline-none focus:ring-0 cursor-text",
                      tabIndex: -1,
                      style: { outline: "none" },
                      children: /* @__PURE__ */ jsx(
                        RichMentionsInput,
                        {
                          value,
                          className: `w-full border-none bg-transparent text-base placeholder:text-muted
                outline-none ring-0 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0
                active:outline-none active:ring-0 focus-within:outline-none focus-within:ring-0
                resize-none overflow-y-auto cursor-text
                ${inputHeightClass} max-h-[200px] transition-all duration-300 ease-in-out`,
                          placeholder,
                          disabled,
                          onKeyDown: handleKeyDown,
                          onInput: handleInputChange,
                          style: {
                            outline: "none",
                            boxShadow: "none",
                            WebkitAppearance: "none",
                            MozAppearance: "none",
                            appearance: "none"
                          },
                          spellCheck: false,
                          autoFocus
                        }
                      )
                    }
                  ),
                  /* @__PURE__ */ jsxs("div", { className: "flex justify-between items-center", children: [
                    /* @__PURE__ */ jsxs("div", { className: "flex items-center", children: [
                      enableFileUpload && /* @__PURE__ */ jsx(
                        Button,
                        {
                          size: "sm",
                          variant: "ghost",
                          onClick: (e) => {
                            e.stopPropagation();
                            handleFileButtonClick();
                          },
                          disabled,
                          type: "button",
                          className: "px-0 mr-2 flex items-center gap-1 theme-static",
                          id: "chat-input-file-button",
                          children: /* @__PURE__ */ jsx(Icon, { name: "paperclip", size: 12 })
                        }
                      ),
                      hasMentions && /* @__PURE__ */ jsx(
                        Button,
                        {
                          size: "sm",
                          variant: "ghost",
                          onClick: (e) => {
                            e.stopPropagation();
                            handleMentionButtonClick();
                          },
                          disabled,
                          type: "button",
                          className: "px-0 mr-2 flex items-center gap-1 theme-static",
                          id: "chat-input-mention-button",
                          children: /* @__PURE__ */ jsx("span", { className: "text-base font-medium", children: "@" })
                        }
                      ),
                      enableFileUpload && /* @__PURE__ */ jsx(
                        "input",
                        {
                          type: "file",
                          ref: fileInputRef,
                          onChange: handleFileInputChange,
                          className: "hidden",
                          multiple: true,
                          accept: fileInputAccept
                        }
                      )
                    ] }),
                    /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
                      model && onModelChange && /* @__PURE__ */ jsx(
                        SelectModelDropdown,
                        {
                          model,
                          disabled: !isGenerating && isSendDisabled,
                          onModelChange
                        }
                      ),
                      /* @__PURE__ */ jsx(
                        Button,
                        {
                          size: "icon-md",
                          icon: buttonIcon,
                          onClick: handleButtonClick,
                          disabled: !isGenerating && isSendDisabled,
                          type: "button",
                          className: "cursor-pointer theme-static",
                          id: "chat-input-send-button"
                        }
                      )
                    ] })
                  ] })
                ]
              }
            )
          ]
        }
      )
    }
  );
};
const ChatInput = ({
  input,
  handleSubmit,
  handleInputChange,
  disabled,
  placeholder = "How can I help you?",
  isLoading,
  isGenerating,
  onStop,
  acceptedFileTypes,
  maxFileSize,
  model,
  onModelChange,
  autoFocus,
  hasMentions
}) => {
  const enableFileUpload = !!acceptedFileTypes;
  const onSubmit = (e, options) => {
    handleSubmit(e, {
      experimental_attachments: options == null ? void 0 : options.attachments
    });
  };
  const onChange = (value) => {
    handleInputChange({ target: { value } });
  };
  return /* @__PURE__ */ jsx(
    ChatInputPrimitive,
    {
      value: input,
      onSubmit,
      onChange,
      disabled,
      placeholder,
      isLoading,
      isGenerating,
      onStop,
      enableFileUpload,
      acceptedFileTypes,
      maxFileSize,
      model,
      onModelChange,
      autoFocus,
      hasMentions
    }
  );
};
const isToolRunning = (state) => {
  return state === "call" || state === "partial-call";
};
const isToolCancelled = (tool) => {
  var _a, _b;
  return tool && tool.state === "result" && ((_b = (_a = tool.result) == null ? void 0 : _a.status) == null ? void 0 : _b.toLowerCase()) === "cancelled";
};
const findFieldLabel = (fieldKey, sheetId, workbooks) => {
  var _a, _b;
  if (!workbooks) return fieldKey;
  for (const workbook of workbooks) {
    const sheet = (_a = workbook.sheets) == null ? void 0 : _a.find((s) => s.id === sheetId);
    if ((_b = sheet == null ? void 0 : sheet.config) == null ? void 0 : _b.fields) {
      const field = sheet.config.fields.find((f) => f.key === fieldKey);
      if (field == null ? void 0 : field.label) {
        return field.label;
      }
    }
  }
  return fieldKey;
};
const formatFieldList = (fieldLabels, joinWithAnd = false) => {
  if (fieldLabels.length === 0) return "";
  if (fieldLabels.length === 1) return fieldLabels[0];
  if (joinWithAnd) {
    if (fieldLabels.length === 2) {
      return `${fieldLabels[0]} and ${fieldLabels[1]}`;
    }
    const lastField = fieldLabels.pop();
    return `${fieldLabels.join(", ")}, and ${lastField}`;
  } else {
    return fieldLabels.join(", ");
  }
};
const getStatusText = (state) => {
  if (state === "running") return "Running";
  if (state === "completed") return "Completed";
  if (state === "cancelled") return "Cancelled";
  return "Failed to run";
};
const generateToggleMessages = (operation, isEnabled, fieldLabels) => {
  const formattedFields = typeof fieldLabels === "string" ? fieldLabels : formatFieldList(fieldLabels, true);
  const actionWord = operation === "required" ? isEnabled ? "required" : "optional" : isEnabled ? "unique" : "non-unique";
  const actionVerb = isEnabled ? "Making" : operation === "required" ? "Making" : "Making";
  const actionVerbPast = isEnabled ? "Made" : operation === "required" ? "Made" : "Made";
  return {
    running: `${actionVerb} ${formattedFields} ${actionWord}`,
    completed: `${actionVerbPast} ${formattedFields} ${actionWord}`,
    failed: `Could not make ${formattedFields} ${actionWord}`,
    cancelled: `${actionVerb} ${formattedFields} ${actionWord} cancelled by user`
  };
};
const getToolMessage = (tool, state, workbooks) => {
  var _a, _b, _c, _d;
  if (tool.toolMessage) {
    if (typeof tool.toolMessage === "string") {
      return tool.toolMessage;
    }
    if (typeof tool.toolMessage === "object" && tool.toolMessage[state]) {
      return tool.toolMessage[state];
    }
  }
  const baseMessages = {
    addExtractor: {
      running: "Adding extractor",
      completed: "Added extractor",
      failed: "Could not add extractor",
      cancelled: "Extractor creation cancelled by user"
    },
    createDocument: {
      running: "Creating document",
      completed: "Created document",
      failed: "Could not create document",
      cancelled: "Document creation cancelled by user"
    },
    createSnapshot: {
      running: "Creating snapshot",
      completed: "Created snapshot",
      failed: "Could not create snapshot",
      cancelled: "Snapshot creation cancelled by user"
    },
    currentDatetime: {
      running: "Getting current date and time",
      completed: "Got current date and time",
      failed: "Could not get current date and time",
      cancelled: "Date/time retrieval cancelled by user"
    },
    customerKBAssistant: {
      running: "Looking up your information",
      completed: "Found relevant information",
      failed: "Could not find relevant information",
      cancelled: "Information lookup cancelled by user"
    },
    deleteDocument: {
      running: "Deleting document",
      completed: "Deleted document",
      failed: "Could not delete document",
      cancelled: "Document deletion cancelled by user"
    },
    deleteRecords: {
      running: "Deleting records",
      completed: "Deleted records",
      failed: "Could not delete records",
      cancelled: "Record deletion cancelled by user"
    },
    deleteRule: {
      running: "Deleting rule",
      completed: "Deleted rule",
      failed: "Could not delete rule",
      cancelled: "Rule deletion cancelled by user"
    },
    deleteSheet: {
      running: "Deleting sheet",
      completed: "Deleted sheet",
      failed: "Could not delete sheet",
      cancelled: "Sheet deletion cancelled by user"
    },
    deployActionListener: {
      running: "Deploying action listener",
      completed: "Deployed action listener",
      failed: "Could not deploy action listener",
      cancelled: "Action listener deployment cancelled by user"
    },
    deployAutocastPlugin: {
      running: "Deploying autocast plugin",
      completed: "Deployed autocast plugin",
      failed: "Could not deploy autocast plugin",
      cancelled: "Autocast plugin deployment cancelled by user"
    },
    deployAutomapPlugin: {
      running: "Deploying automap plugin",
      completed: "Deployed automap plugin",
      failed: "Could not deploy automap plugin",
      cancelled: "Automap plugin deployment cancelled by user"
    },
    deployWebhookEgressPlugin: {
      running: "Deploying webhook egress plugin",
      completed: "Deployed webhook egress plugin",
      failed: "Could not deploy webhook egress plugin",
      cancelled: "Webhook egress plugin deployment cancelled by user"
    },
    flatfileDocumentation: {
      running: "Searching Flatfile documentation",
      completed: "Found documentation",
      failed: "Could not find documentation",
      cancelled: "Documentation search cancelled by user"
    },
    flatfileNodeAssistant: {
      running: "Looking up Flatfile documentation",
      completed: "Found relevant documentation",
      failed: "Could not find relevant documentation",
      cancelled: "Documentation lookup cancelled by user"
    },
    flatfilePlugins: {
      running: "Looking up Flatfile plugins",
      completed: "Found relevant plugins",
      failed: "Could not find relevant plugins",
      cancelled: "Plugin lookup cancelled by user"
    },
    generateActionConfig: {
      running: "Generating action configuration",
      completed: "Generated action configuration",
      failed: "Could not generate action configuration",
      cancelled: "Action configuration generation cancelled by user"
    },
    generateGuide: {
      running: "Generating guide",
      completed: "Generated guide",
      failed: "Could not generate guide",
      cancelled: "Guide generation cancelled by user"
    },
    generateRule: {
      running: "Generating a rule",
      completed: "Generated a rule",
      failed: "Could not generate a rule",
      cancelled: "Rule generation cancelled by user"
    },
    getRecords: {
      running: "Getting records",
      completed: "Got records",
      failed: "Could not get records",
      cancelled: "Record retrieval cancelled by user"
    },
    getRules: {
      running: "Getting rules",
      completed: "Got rules",
      failed: "Could not get rules",
      cancelled: "Rule retrieval cancelled by user"
    },
    listDocuments: {
      running: "Getting documents",
      completed: "Got documents",
      failed: "Could not get documents",
      cancelled: "Document retrieval cancelled by user"
    },
    loadBlueprint: {
      running: "Reading your workbook configuration",
      completed: "Read workbook configuration",
      failed: "Could not read workbook configuration",
      cancelled: "Workbook configuration retrieval cancelled by user"
    },
    makeCompositeUnique: {
      running: "Making composite unique constraint",
      completed: "Made composite unique constraint",
      failed: "Could not make composite unique constraint",
      cancelled: "Composite unique constraint creation cancelled by user"
    },
    patchWorkbook: {
      running: "Updating workbook",
      completed: "Updated workbook",
      failed: "Could not update workbook",
      cancelled: "Workbook update cancelled by user"
    },
    updateWorkbook: {
      running: "Checking workbook",
      completed: "Checked workbook",
      failed: "Could not check workbook",
      cancelled: "Workbook update cancelled by user"
    },
    renameField: {
      running: "Renaming a field",
      completed: "Renamed a field",
      failed: "Could not rename a field",
      cancelled: "Field renaming cancelled by user"
    },
    tavilySearch: {
      running: "Searching for information",
      completed: "Found information",
      failed: "Could not find information",
      cancelled: "Information search cancelled by user"
    },
    transformPreview: {
      running: ((_a = tool.args) == null ? void 0 : _a.action) ?? "Generating preview",
      completed: "Generated preview",
      failed: "Could not generate preview",
      cancelled: "Preview generation cancelled by user"
    },
    updateDocument: {
      running: "Updating document",
      completed: "Updated document",
      failed: "Could not update document",
      cancelled: "Document update cancelled by user"
    },
    updateSheet: {
      running: "Updating sheet",
      completed: "Updated sheet",
      failed: "Could not update sheet",
      cancelled: "Sheet update cancelled by user"
    },
    updateSheetOrder: {
      running: "Updating sheet order",
      completed: "Updated sheet order",
      failed: "Could not update sheet order",
      cancelled: "Sheet order update cancelled by user"
    },
    requestProgramApproval: {
      running: "Requesting program approval",
      completed: "Requested program approval",
      failed: "Could not request program approval",
      cancelled: "Program approval request cancelled by user"
    }
  };
  const isCancelled = isToolCancelled(tool);
  const effectiveState = isCancelled ? "cancelled" : state;
  if (tool.args) {
    if (Array.isArray(tool.args.fieldKeys) && tool.args.sheetId) {
      const fieldLabels = tool.args.fieldKeys.map(
        (key) => findFieldLabel(key, tool.args.sheetId, workbooks)
      );
      const formattedFields = formatFieldList(fieldLabels, true);
      switch (tool.toolName) {
        case "toggleRequired":
          return generateToggleMessages(
            "required",
            tool.args.required,
            fieldLabels
          )[effectiveState];
        case "toggleUnique":
          return generateToggleMessages(
            "unique",
            tool.args.unique,
            fieldLabels
          )[effectiveState];
        case "generateRule":
          return {
            running: `Generating rule on ${formattedFields}`,
            completed: `Generated rule on ${formattedFields}`,
            failed: `Could not generate rule on ${formattedFields}`,
            cancelled: `Rule generation on ${formattedFields} cancelled by user`
          }[effectiveState];
        default:
          if (isCancelled) {
            return `${tool.toolName} cancelled by user`;
          }
          return ((_b = baseMessages[tool.toolName]) == null ? void 0 : _b[effectiveState]) || `${getStatusText(effectiveState)} ${tool.toolName}`;
      }
    }
    const fieldLabel = tool.args.sheetId && tool.args.fieldKey ? findFieldLabel(tool.args.fieldKey, tool.args.sheetId, workbooks) : tool.args.fieldKey || "field";
    switch (tool.toolName) {
      case "toggleRequired":
        return generateToggleMessages(
          "required",
          tool.args.required,
          fieldLabel
        )[effectiveState];
      case "toggleUnique":
        return generateToggleMessages("unique", tool.args.unique, fieldLabel)[effectiveState];
      case "renameField":
        if (isCancelled) {
          return `Renaming ${fieldLabel} cancelled by user`;
        }
        return {
          running: `Renaming ${fieldLabel} to ${tool.args.newName || "new name"}`,
          completed: `Renamed to ${fieldLabel}`,
          failed: `Could not rename ${fieldLabel}`,
          cancelled: `Renaming ${fieldLabel} cancelled by user`
        }[effectiveState];
      case "generateRule":
        return {
          running: `Generating rule on ${fieldLabel}`,
          completed: `Generated rule on ${fieldLabel}`,
          failed: `Could not generate rule on ${fieldLabel}`,
          cancelled: `Rule generation on ${fieldLabel} cancelled by user`
        }[effectiveState];
      default:
        if (isCancelled) {
          return `${tool.toolName} cancelled by user`;
        }
        return ((_c = baseMessages[tool.toolName]) == null ? void 0 : _c[effectiveState]) || `${getStatusText(effectiveState)} ${tool.toolName}`;
    }
  }
  if (isCancelled) {
    return `${tool.toolName} cancelled by user`;
  }
  return ((_d = baseMessages[tool.toolName]) == null ? void 0 : _d[effectiveState]) || `${getStatusText(effectiveState)} ${tool.toolName}`;
};
const CHART_COLORS = [
  "#000000",
  // Black
  "#404040",
  // Dark gray
  "#808080",
  // Medium gray
  "#BFBFBF",
  // Light gray
  "#EEEEEE",
  // Gray
  "#202020",
  // Very dark gray
  "#606060",
  // Darker medium gray
  "#9F9F9F",
  // Lighter medium gray
  "#DFDFDF",
  // Very light gray
  "#333333"
  // Dark gray alternative
];
function getColor(index) {
  return CHART_COLORS[index % CHART_COLORS.length];
}
function AreaChartComponent({
  data,
  config,
  nameKey,
  title,
  gradient = false,
  isLoading = false
}) {
  const dataKeys = Object.keys(config);
  const useGradient = gradient !== false;
  const startOpacity = typeof gradient === "object" ? gradient.startOpacity ?? 0.8 : 0.8;
  const endOpacity = typeof gradient === "object" ? gradient.endOpacity ?? 0.1 : 0.1;
  return /* @__PURE__ */ jsxs("div", { className: "m-2 px-4 py-6 rounded-lg border border-border", children: [
    title && /* @__PURE__ */ jsx("div", { className: "text-sm font-normal text-left mb-4 text-muted-foreground", children: title }),
    isLoading ? /* @__PURE__ */ jsxs("div", { className: "w-full h-[200px] flex flex-col gap-2", children: [
      /* @__PURE__ */ jsx("div", { className: "mt-auto h-6 flex justify-between items-center px-4", children: Array.from({ length: 6 }).map((_, i) => /* @__PURE__ */ jsx(
        "div",
        {
          className: "h-2 w-8 bg-gray-200 rounded animate-pulse"
        },
        i
      )) }),
      /* @__PURE__ */ jsx("div", { className: "flex-1 w-full bg-gray-100 rounded relative overflow-hidden", children: /* @__PURE__ */ jsx("div", { className: "absolute inset-0", children: /* @__PURE__ */ jsx("div", { className: "w-full h-full flex items-end", children: /* @__PURE__ */ jsx("div", { className: "w-full h-[70%] bg-gray-200 rounded-t-lg animate-pulse opacity-70" }) }) }) })
    ] }) : /* @__PURE__ */ jsx(ChartContainer, { config, children: /* @__PURE__ */ jsxs(
      AreaChart,
      {
        accessibilityLayer: true,
        data,
        margin: { left: 24, right: 12 },
        children: [
          /* @__PURE__ */ jsx(CartesianGrid, { vertical: false, stroke: "rgba(0,0,0,0.05)" }),
          /* @__PURE__ */ jsx(
            XAxis,
            {
              dataKey: nameKey,
              tickLine: false,
              axisLine: false,
              tickMargin: 8
            }
          ),
          /* @__PURE__ */ jsx(
            ChartTooltip,
            {
              cursor: false,
              content: /* @__PURE__ */ jsx(ChartTooltipContent, { indicator: "dot" })
            }
          ),
          useGradient && /* @__PURE__ */ jsx("defs", { children: dataKeys.map((key, index) => /* @__PURE__ */ jsxs(
            "linearGradient",
            {
              id: `gradient-${key}`,
              x1: "0",
              y1: "0",
              x2: "0",
              y2: "1",
              children: [
                /* @__PURE__ */ jsx(
                  "stop",
                  {
                    offset: "5%",
                    stopColor: getColor(index),
                    stopOpacity: startOpacity
                  }
                ),
                /* @__PURE__ */ jsx(
                  "stop",
                  {
                    offset: "95%",
                    stopColor: getColor(index),
                    stopOpacity: endOpacity
                  }
                )
              ]
            },
            key
          )) }),
          dataKeys.map((key, index) => /* @__PURE__ */ jsx(
            Area,
            {
              isAnimationActive: false,
              dataKey: key,
              type: "natural",
              fill: useGradient ? `url(#gradient-${key})` : getColor(index),
              fillOpacity: useGradient ? 1 : 0.4,
              stroke: getColor(index),
              stackId: "a"
            },
            key
          ))
        ]
      }
    ) })
  ] });
}
function BarChartComponent({
  data,
  config,
  nameKey,
  title,
  incrementalSize = false,
  baseBarSize = 16,
  isLoading = false
}) {
  const dataKeys = Object.keys(config);
  return /* @__PURE__ */ jsxs("div", { className: "m-2 px-4 py-6 rounded-lg border border-border", children: [
    title && /* @__PURE__ */ jsx("div", { className: "text-sm font-normal text-left mb-4 text-muted-foreground", children: title }),
    isLoading ? /* @__PURE__ */ jsxs("div", { className: "w-full h-[200px] flex flex-col gap-2", children: [
      /* @__PURE__ */ jsx("div", { className: "mt-auto h-6 flex justify-between items-center px-4", children: Array.from({ length: 6 }).map((_, i) => /* @__PURE__ */ jsx(
        "div",
        {
          className: "h-2 w-8 bg-gray-200 rounded animate-pulse"
        },
        i
      )) }),
      /* @__PURE__ */ jsx("div", { className: "flex-1 w-full flex items-end justify-around p-2", children: Array.from({ length: 6 }).map((_, i) => /* @__PURE__ */ jsx(
        "div",
        {
          className: "w-8 bg-gray-200 rounded-t animate-pulse",
          style: {
            height: `${30 + Math.random() * 60}%`,
            opacity: 0.7
          }
        },
        i
      )) })
    ] }) : /* @__PURE__ */ jsx(ChartContainer, { config, children: /* @__PURE__ */ jsxs(
      BarChart,
      {
        accessibilityLayer: true,
        data,
        barCategoryGap: 10,
        barGap: 10,
        children: [
          /* @__PURE__ */ jsx(CartesianGrid, { vertical: false, stroke: "rgba(0,0,0,0.05)" }),
          /* @__PURE__ */ jsx(
            XAxis,
            {
              dataKey: nameKey,
              tickLine: false,
              tickMargin: 20,
              axisLine: false
            }
          ),
          /* @__PURE__ */ jsx(
            ChartTooltip,
            {
              cursor: false,
              content: /* @__PURE__ */ jsx(ChartTooltipContent, { indicator: "dashed" })
            }
          ),
          dataKeys.map((key, index) => /* @__PURE__ */ jsx(
            Bar,
            {
              isAnimationActive: false,
              dataKey: key,
              radius: 4,
              barSize: incrementalSize ? baseBarSize + index * 2 : baseBarSize,
              fillOpacity: 0.7,
              fill: getColor(index)
            },
            key
          ))
        ]
      }
    ) })
  ] });
}
function PieChartComponent({
  data,
  config,
  dataKey,
  nameKey,
  title,
  isLoading = false
}) {
  return /* @__PURE__ */ jsxs("div", { className: "m-2 px-4 py-6 rounded-lg border border-border", children: [
    title && /* @__PURE__ */ jsx("div", { className: "text-sm font-normal text-left mb-4 text-muted-foreground", children: title }),
    isLoading ? /* @__PURE__ */ jsxs("div", { className: "w-full flex flex-col items-center justify-center", children: [
      /* @__PURE__ */ jsxs("div", { className: "relative mx-auto aspect-square max-h-[200px] w-[200px]", children: [
        /* @__PURE__ */ jsx("div", { className: "absolute inset-0 rounded-full bg-gray-200 animate-pulse" }),
        /* @__PURE__ */ jsx("div", { className: "absolute inset-[30%] rounded-full bg-white" })
      ] }),
      /* @__PURE__ */ jsx("div", { className: "mt-6 w-full flex flex-wrap justify-center gap-2", children: Array.from({ length: 4 }).map((_, i) => /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
        /* @__PURE__ */ jsx("div", { className: "h-3 w-3 rounded-full bg-gray-200 animate-pulse" }),
        /* @__PURE__ */ jsx("div", { className: "h-2 w-16 bg-gray-200 rounded animate-pulse" })
      ] }, i)) })
    ] }) : /* @__PURE__ */ jsx(
      ChartContainer,
      {
        config,
        className: "mx-auto aspect-square max-h-[250px]",
        children: /* @__PURE__ */ jsxs(PieChart, { children: [
          title && /* @__PURE__ */ jsx("div", { className: "text-base font-medium text-left mb-4", children: title }),
          /* @__PURE__ */ jsx(
            ChartTooltip,
            {
              cursor: false,
              content: /* @__PURE__ */ jsx(ChartTooltipContent, { hideLabel: true })
            }
          ),
          /* @__PURE__ */ jsx(
            Pie,
            {
              isAnimationActive: false,
              data,
              dataKey,
              nameKey,
              innerRadius: 40,
              strokeWidth: 20,
              children: data.map((_, index) => /* @__PURE__ */ jsx(Cell, { fill: getColor(index * 2) }, `cell-${index}`))
            }
          ),
          /* @__PURE__ */ jsx(
            ChartLegend,
            {
              content: renderLegend,
              className: "-translate-y-2 flex-wrap gap-2 [&>*]:basis-1/4 [&>*]:justify-center"
            }
          )
        ] })
      }
    )
  ] });
}
const renderLegend = (props) => {
  const { payload } = props;
  const scrollContainerRef = useRef(null);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const updateScrollIndicators = () => {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      setCanScrollLeft(scrollLeft > 10);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    };
    updateScrollIndicators();
    container.addEventListener("scroll", updateScrollIndicators);
    window.addEventListener("resize", updateScrollIndicators);
    return () => {
      container.removeEventListener("scroll", updateScrollIndicators);
      window.removeEventListener("resize", updateScrollIndicators);
    };
  }, [payload]);
  return /* @__PURE__ */ jsx("div", { className: "w-full pt-3 flex justify-center", children: /* @__PURE__ */ jsxs("div", { className: "relative max-w-full", children: [
    /* @__PURE__ */ jsx(
      "div",
      {
        ref: scrollContainerRef,
        className: "flex overflow-x-auto gap-4 pb-2 hide-scrollbar snap-x",
        style: { scrollbarWidth: "none" },
        children: payload == null ? void 0 : payload.map((entry, index) => /* @__PURE__ */ jsxs(
          "div",
          {
            className: "flex items-center gap-1.5 flex-shrink-0 snap-start",
            children: [
              /* @__PURE__ */ jsx(
                "div",
                {
                  className: "h-2 w-2 shrink-0 rounded-[2px]",
                  style: { backgroundColor: entry.color }
                }
              ),
              /* @__PURE__ */ jsx("span", { className: "text-sm whitespace-nowrap", children: entry.value })
            ]
          },
          `item-${index}`
        ))
      }
    ),
    canScrollLeft && /* @__PURE__ */ jsx("div", { className: "absolute left-0 top-2 -translate-y-1/2 animate-pulse", children: /* @__PURE__ */ jsx("div", { className: "w-1 h-3 bg-gray-200 rounded-full" }) }),
    canScrollRight && /* @__PURE__ */ jsx("div", { className: "absolute right-0 top-2 -translate-y-1/2 animate-pulse", children: /* @__PURE__ */ jsx("div", { className: "w-1 h-3 bg-gray-200 rounded-full" }) })
  ] }) });
};
const DataVizChart = ({
  data,
  width,
  height,
  showLegend,
  animated
}) => {
  return /* @__PURE__ */ jsxs(PieChart, { width, height, children: [
    /* @__PURE__ */ jsx(
      Pie,
      {
        dataKey: "value",
        isAnimationActive: animated,
        data,
        cx: "50%",
        cy: "50%",
        outerRadius: width > 400 ? 200 : 80,
        fill: "#8884d8"
      }
    ),
    /* @__PURE__ */ jsx(Tooltip, {}),
    showLegend && /* @__PURE__ */ jsx(Legend, {})
  ] });
};
const DataViz = ({ data }) => {
  const [expandedPart, setExpandedPart] = useState(null);
  return /* @__PURE__ */ jsxs("div", { children: [
    /* @__PURE__ */ jsx(
      "div",
      {
        className: "overflow-x-auto flex gap-4 pb-4 max-w-[320px]",
        style: { maxWidth: "320px" },
        children: data.map((part, index) => /* @__PURE__ */ jsxs(
          "div",
          {
            className: "flex-none bg-white rounded-lg border border-gray-200 overflow-hidden",
            style: { minWidth: "238px" },
            children: [
              /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between p-4 border-b border-gray-100", children: [
                /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
                  /* @__PURE__ */ jsx("span", { className: "text-gray-500", children: /* @__PURE__ */ jsx(Icon, { name: "analysis", size: 16 }) }),
                  /* @__PURE__ */ jsx("span", { className: "text-base font-medium text-gray-900", children: part.name })
                ] }),
                /* @__PURE__ */ jsx(
                  "button",
                  {
                    onClick: () => setExpandedPart(part),
                    className: "text-gray-500 hover:text-gray-700 p-1 hover:bg-gray-100 rounded-full transition-colors",
                    children: /* @__PURE__ */ jsx(Icon, { name: "expand", size: 16 })
                  }
                )
              ] }),
              /* @__PURE__ */ jsx("div", { className: "p-4", children: /* @__PURE__ */ jsx(
                DataVizChart,
                {
                  data: part.data,
                  width: 200,
                  height: 200,
                  showLegend: false,
                  animated: false
                }
              ) })
            ]
          },
          index
        ))
      }
    ),
    /* @__PURE__ */ jsx(
      Dialog,
      {
        open: expandedPart !== null,
        onOpenChange: () => setExpandedPart(null),
        children: /* @__PURE__ */ jsxs(DialogContent, { className: "w-[80vw] max-w-[80vw] p-0 gap-0", children: [
          /* @__PURE__ */ jsx(DialogHeader, { className: "p-6 pb-2", children: /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between", children: [
            /* @__PURE__ */ jsx(DialogTitle, { className: "text-xl font-semibold", children: expandedPart == null ? void 0 : expandedPart.name }),
            /* @__PURE__ */ jsx(
              Button,
              {
                variant: "ghost",
                size: "icon-md",
                onClick: () => setExpandedPart(null),
                className: "h-8 w-8 p-0 theme-static",
                children: /* @__PURE__ */ jsx(Icon, { name: "cross-large" })
              }
            )
          ] }) }),
          /* @__PURE__ */ jsx("div", { className: "p-6 pt-2", children: expandedPart && /* @__PURE__ */ jsx("div", { className: "flex flex-col items-center", children: /* @__PURE__ */ jsx(
            DataVizChart,
            {
              data: expandedPart.data,
              width: 800,
              height: 500,
              showLegend: true,
              animated: true
            }
          ) }) })
        ] })
      }
    )
  ] });
};
const MENTION_STYLES = {
  // Active mention (solid background)
  ACTIVE: "mention -mt-0.5 inline-block bg-accent-90 border border-border text-accent-foreground text-[13px] font-medium rounded-xs px-1 py-0 mx-0.5 align-middle whitespace-normal",
  // Pending mention (lighter background)
  PENDING: "mention -mt-0.5 inline-block bg-accent/50 border border-border text-accent-foreground text-[13px] font-medium rounded-xs px-1 py-0 mx-0.5 align-middle whitespace-normal",
  // Heading mention (inherits font properties from parent heading except size)
  HEADING: "mention -mt-0.5 inline-block bg-accent-90 border border-border text-accent-foreground text-[13px] font-semibold rounded-xs px-1 py-0 mx-0.5 align-middle whitespace-normal",
  // Emphasized mention (for strong/bold text but with consistent font size)
  EMPHASIZED: "mention -mt-0.5 inline-block bg-accent-90 border border-border text-accent-foreground text-[13px] font-semibold rounded-xs px-1 py-0 mx-0.5 align-middle whitespace-normal",
  // Code mention (for code blocks, uses monospace font)
  CODE: "mention -mt-0.5 inline-block bg-accent-90 border border-border text-accent-foreground font-mono text-[13px] rounded-xs px-1 py-0 mx-0.5 align-middle whitespace-normal",
  // List mention (for list items with specific line height)
  LIST: "mention -mt-0.5 inline-block bg-accent-90 border border-border text-accent-foreground text-[13px] font-medium rounded-xs px-1 py-0 mx-0.5 align-middle whitespace-normal leading-[20px]"
};
const MENTION_REGEX = {
  /**
   * Matches only the standard 3-bracket syntax:
   * - @{{{...}}} (standardized 3-bracket syntax)
   * Made more permissive to handle potential line breaks and spacing
   * Limited to 1024 characters to prevent ReDoS attacks
   */
  ANY_MENTION: /@\{{{[\s\S]{1,1024}?\}}/g,
  /**
   * Matches the standard 3-bracket syntax:
   * - @{{{...}}} (standardized 3-bracket syntax)
   * More permissive pattern that can handle various content
   * Limited to 1024 characters to prevent ReDoS attacks
   */
  ENCODED: /@\{{3}[\s\S]{1,1024}?\}{3}/g,
  /**
   * Simple test pattern to quickly check if text might contain mentions with 3 brackets
   */
  QUICK_TEST: /@\{{3}/,
  /**
   * Pattern to detect the start of a potentially incomplete mention
   */
  INCOMPLETE_START: /@\{(?!\{\{)/,
  /**
   * Pattern to detect an incomplete mention that's missing closing braces
   * Limited to 1024 characters to prevent excessive backtracking
   */
  PARTIAL_MENTION: /@\{{1,3}[^}]{0,1024}$/
};
const containsPartialMention = (text) => {
  if (!text) return false;
  if (text.match(/@\{{1,3}[^}]{0,1024}$/)) {
    return true;
  }
  const openingCount = (text.match(/@\{/g) || []).length;
  const closingCount = (text.match(/}/g) || []).length;
  return openingCount > closingCount;
};
const formatMentionHTML = (item) => {
  const displayName = item.displayText || (item == null ? void 0 : item.name) || "";
  const dataValue = item.ref;
  return `<span class="${MENTION_STYLES.ACTIVE}" 
    data-ref="${item.ref}" 
    data-type="${item.type || ""}" 
    data-display-text="${displayName}"
    data-encoded-mention="${encodeURIComponent(dataValue)}" 
    contenteditable="false">${displayName}</span>`;
};
const formatMentionText = (item) => {
  if (item.ref && item.ref.startsWith("@{{{") && item.ref.endsWith("}}}")) {
    return item.ref;
  }
  const mentionData = {
    type: item.type,
    displayText: item.displayText,
    ...item.groupId ? { groupId: item.groupId } : {},
    ...item.groupName ? { groupName: item.groupName } : {}
  };
  return `@{{${JSON.stringify(mentionData)}}}`;
};
const processMentionsInHTML = (html) => {
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;
  const mentionSpans = tempDiv.querySelectorAll("span.mention");
  const richMentionSpans = tempDiv.querySelectorAll("span[data-rich-mentions]");
  mentionSpans.forEach(processSpan);
  richMentionSpans.forEach(processSpan);
  function processSpan(span) {
    var _a, _b, _c;
    const encodedMention = span.getAttribute("data-encoded-mention");
    if (encodedMention) {
      const decodedMention = decodeURIComponent(encodedMention);
      if (decodedMention.startsWith("@{{{") && decodedMention.endsWith("}}}")) {
        const mentionText = document.createTextNode(decodedMention);
        (_a = span.parentNode) == null ? void 0 : _a.replaceChild(mentionText, span);
        return;
      }
    }
    const richMention = span.getAttribute("data-rich-mentions");
    if (richMention && richMention.startsWith("@{{{") && richMention.endsWith("}}}")) {
      const mentionText = document.createTextNode(richMention);
      (_b = span.parentNode) == null ? void 0 : _b.replaceChild(mentionText, span);
      return;
    }
    const dataDisplayText = span.getAttribute("data-display-text");
    const ref = span.getAttribute("data-ref");
    const type = span.getAttribute("data-type");
    if (ref && type) {
      const mentionObj = {
        ref,
        displayText: dataDisplayText || ref,
        type
      };
      const groupName = span.getAttribute("data-group-name");
      const groupId = span.getAttribute("data-group-id");
      if (groupName) mentionObj.groupName = groupName;
      if (groupId) mentionObj.groupId = groupId;
      const formattedMention = formatMentionText(mentionObj);
      const mentionText = document.createTextNode(formattedMention);
      (_c = span.parentNode) == null ? void 0 : _c.replaceChild(mentionText, span);
    }
  }
  return tempDiv.innerText;
};
const styleMentionElement = (element, isFinal) => {
  if (isFinal) {
    const ref = element.getAttribute("data-rich-mentions");
    if (ref && ref.startsWith("@{{{") && ref.endsWith("}}}")) {
      try {
        const jsonContent = ref.slice(4, -3);
        const mentionData = JSON.parse(`{${jsonContent}}`);
        if (mentionData.displayText) {
          element.textContent = mentionData.displayText;
        }
      } catch (error) {
        console.warn("Failed to parse mention data:", error);
      }
    }
  }
  element.className = isFinal ? MENTION_STYLES.ACTIVE : MENTION_STYLES.PENDING;
};
const createMentionConfig = (mentionItems) => {
  return {
    query: /@([a-zA-Z0-9_\s-]*)$/,
    // Match our JSON ref format: @{{{"..."}}}
    match: /@\{{3}[\s\S]*?\}{3}/g,
    // Use a replacement pattern that extracts displayText from the JSON
    matchDisplay: "$&",
    // We'll handle this in transformFinalFragment with a custom approach
    markup: formatMentionHTML,
    content: (item) => {
      return item.ref;
    },
    customizeFragment: styleMentionElement,
    onMention: (str) => {
      const search = str.slice(1).toLowerCase().replace(/\u00A0/g, " ");
      const filteredMentions = mentionItems.filter((mention) => {
        return mention.displayText.toLowerCase().includes(search) || mention.groupName && mention.groupName.toLowerCase().includes(search);
      });
      if (filteredMentions.length === 0) {
        return [
          {
            ref: "no_results",
            displayText: "No results found",
            name: "No results found"
            // Add name for compatibility with RichMentionsContext
          }
        ];
      }
      const sortedMentions = [...filteredMentions].sort((a, b) => {
        if (a.displayText.toLowerCase() === search) return -1;
        if (b.displayText.toLowerCase() === search) return 1;
        if (a.displayText.toLowerCase().startsWith(search)) return -1;
        if (b.displayText.toLowerCase().startsWith(search)) return 1;
        return a.displayText.localeCompare(b.displayText);
      });
      return sortedMentions.slice(0, 10).map((mention) => ({
        ...mention,
        name: mention.displayText || ""
        // Ensure name is present for compatibility
      }));
    }
  };
};
const getDisplayNameFromMention = (mentionStr) => {
  try {
    const mentionData = extractMentionData(mentionStr);
    if (mentionData) {
      if (mentionData.displayText) {
        return mentionData.displayText;
      } else if (mentionData.name) {
        return mentionData.name;
      }
    }
    return "Unknown Mention";
  } catch (e) {
    return "Unknown Mention";
  }
};
function extractMentionData(mentionText) {
  if (!mentionText) return null;
  try {
    const text = mentionText.startsWith("@") ? mentionText.slice(1) : mentionText;
    const match = text.match(/\{{3}(.*?)\}{3}/);
    if (match && match[1]) {
      const parsedData = JSON.parse(`{${match[1]}}`);
      if (parsedData.type === "field") {
        if (!parsedData.name && parsedData.key) {
          parsedData.name = parsedData.key;
        }
        if (parsedData.name && !parsedData.fieldKey) {
          parsedData.fieldKey = parsedData.name;
        }
      }
      return parsedData;
    }
  } catch (error) {
  }
  return null;
}
function MentionFormatter({
  text,
  className = "",
  debug = false,
  onMentionClick,
  isHeading = false,
  isEmphasized = false,
  isCode = false,
  isList = false
}) {
  if (!text) return null;
  if (debug) {
    return /* @__PURE__ */ jsxs("div", { className, children: [
      /* @__PURE__ */ jsxs("div", { children: [
        "Original: ",
        text
      ] }),
      /* @__PURE__ */ jsxs("div", { children: [
        "Display Name: ",
        getDisplayNameFromMention(text)
      ] })
    ] });
  }
  const handleMentionClick = (mentionText) => {
    if (!onMentionClick) return;
    try {
      const mentionData = extractMentionData(mentionText);
      if (mentionData) {
        onMentionClick(mentionData);
      }
    } catch (error) {
    }
  };
  const getMentionStyle = () => {
    if (isHeading) {
      return MENTION_STYLES.HEADING;
    } else if (isCode) {
      return MENTION_STYLES.CODE;
    } else if (isEmphasized) {
      return MENTION_STYLES.EMPHASIZED;
    } else if (isList) {
      return MENTION_STYLES.LIST;
    }
    return MENTION_STYLES.ACTIVE;
  };
  const mentionMatches = text.match(MENTION_REGEX.ENCODED);
  if (!mentionMatches || mentionMatches.length === 0) {
    return /* @__PURE__ */ jsx("span", { className, children: text });
  }
  if (mentionMatches.length === 1 && mentionMatches[0] === text) {
    const displayName = getDisplayNameFromMention(text);
    return /* @__PURE__ */ jsx(
      "span",
      {
        className: `${getMentionStyle()} ${className} ${onMentionClick ? "cursor-pointer hover:bg-accent/80" : ""} inline-block align-middle inherit-typography text-[13px]`,
        onClick: onMentionClick ? () => handleMentionClick(text) : void 0,
        children: displayName
      }
    );
  }
  const segments = [];
  let lastIndex = 0;
  let match;
  const regex = new RegExp(MENTION_REGEX.ENCODED);
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      segments.push({
        type: "text",
        content: text.substring(lastIndex, match.index)
      });
    }
    segments.push({
      type: "mention",
      content: match[0]
    });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    segments.push({
      type: "text",
      content: text.substring(lastIndex)
    });
  }
  return /* @__PURE__ */ jsx("span", { className: `inline-block flex-wrap items-center ${className}`, children: segments.map((segment, index) => {
    if (segment.type === "mention") {
      const displayName = getDisplayNameFromMention(segment.content);
      return /* @__PURE__ */ jsx(
        "span",
        {
          className: `${getMentionStyle()} ${onMentionClick ? "cursor-pointer hover:bg-accent/80" : ""} align-middle inherit-typography text-[13px]`,
          onClick: onMentionClick ? () => handleMentionClick(segment.content) : void 0,
          children: displayName
        },
        index
      );
    }
    return /* @__PURE__ */ jsx("span", { className: "break-words", children: segment.content }, index);
  }) });
}
function parseMarkdownIntoBlocks(markdown) {
  if (!markdown) return [];
  const processedMarkdown = markdown.replace(/(\d+\.\s*)(@\{{3}[^}}]{0,1024}\}{3})/g, "$1$2").replace(/([-*]\s*)(@\{{3}[^}}]{0,1024}\}{3})/g, "$1$2");
  try {
    const tokens = marked.lexer(processedMarkdown);
    return tokens.map((token) => {
      if (token.type === "list") {
        return `<!--LIST_ITEM-->${token.raw}`;
      }
      return token.raw;
    }).filter((block) => block.trim().length > 0);
  } catch {
    return [sanitizeIncompleteBlock(markdown)];
  }
}
function sanitizeIncompleteBlock(block) {
  if (!containsPartialMention(block)) {
    return block;
  }
  const lastCompleteMentionEnd = block.lastIndexOf("}}}");
  const lastMentionStart = block.lastIndexOf("@{{{");
  if (lastMentionStart > lastCompleteMentionEnd) {
    block = block.substring(0, lastMentionStart);
  }
  block = block.replace(/@{{{[^}]{0,1024}$/g, "");
  return block;
}
function containsTableMarkup(content) {
  if (!content) return false;
  const lines = content.split("\n");
  let tableLikeLines = 0;
  const headerRowPattern = /^\s*\|[-:\s|]{0,1000}\|\s*$/;
  const tableRowPattern = /^\s*\|[^|]{0,1000}\|[^|]{0,1000}\|/;
  for (const line of lines) {
    if (line.includes("**") || line.includes("@{{{")) {
      continue;
    }
    const pipeCount = (line.match(/\|/g) || []).length;
    if (pipeCount > 2) {
      tableLikeLines++;
    }
    if (headerRowPattern.test(line)) {
      return true;
    }
    if (tableRowPattern.test(line)) {
      tableLikeLines++;
    }
  }
  return tableLikeLines >= 2;
}
function convertTableToCodeBlock(content) {
  return "```\n" + content + "\n```";
}
function renderMixedContentWithMentions(content) {
  if (!content || !content.includes("@{{{")) {
    return content;
  }
  return content.replace(new RegExp(MENTION_REGEX.ENCODED, "g"), (match) => {
    return match;
  });
}
function isParagraphBlock(block) {
  if (!block) return false;
  return !block.startsWith("<!--LIST_ITEM-->") && !block.startsWith("```") && !block.startsWith("#") && !containsTableMarkup(block) && block.trim().length > 0;
}
function getBlockType(block) {
  if (!block) return "";
  if (block.startsWith("<!--LIST_ITEM-->")) return "list";
  if (block.startsWith("#")) return "heading";
  if (block.startsWith("```")) return "code";
  if (containsTableMarkup(block)) return "table";
  if (isParagraphBlock(block)) return "paragraph";
  return "other";
}
function isListComplete(listContent) {
  try {
    const nestedListCount = (listContent.match(/\n\s{2,}[-*]/g) || []).length;
    const nestedListEndCount = (listContent.match(/\n\n|\n$/g) || []).length;
    if (nestedListCount > 0 && nestedListEndCount < nestedListCount) {
      return false;
    }
    const orderedListMatches = listContent.match(/\n\d+\./g) || [];
    if (orderedListMatches.length > 0) {
      const numbers = orderedListMatches.map(
        (match) => parseInt(match.replace("\n", "").replace(".", ""))
      );
      let previousNum = 0;
      for (const num of numbers) {
        if (num !== previousNum + 1 && num !== 1) {
          return false;
        }
        previousNum = num;
      }
    }
    return true;
  } catch (e) {
    return false;
  }
}
function blockContainsMentions(block) {
  if (block.includes("<!--LIST_ITEM-->") && block.includes("@{{{")) {
    return true;
  }
  if ((block == null ? void 0 : block.includes("@{{{")) && (block == null ? void 0 : block.includes("}}}"))) {
    const startPos = block.indexOf("@{{{");
    const endPos = block.indexOf("}}}", startPos) + 3;
    const extracted = block.substring(startPos, endPos);
    const manualFound = (extracted == null ? void 0 : extracted.startsWith("@{{{")) && (extracted == null ? void 0 : extracted.endsWith("}}}"));
    const regexResult = MENTION_REGEX.ENCODED.test(block);
    return regexResult || manualFound;
  }
  return MENTION_REGEX.ENCODED.test(block);
}
function generateBlockKey(block, index) {
  return `md-block-${index}-${block.length}`;
}
function sanitizeMentions(content) {
  if (!content) return "";
  return content.replace(/^(.{0,10000}:)$/gm, (match) => {
    if (!match.trim().startsWith("#")) {
      return match.replace(/:/g, "\\:");
    }
    return match;
  }).replace(
    /(^|\n)(\s*[-*]|\s*\d+\.)\s+(\*\*[^*\n]+)(\*\*)(:)(\s+)/gm,
    "$1$2 $3:$4$6"
  ).replace(/(\*\*[^*]{0,1024}(?:\*[^*]{0,100}){0,10}\*\*)(:)/g, "$1\\$2").replace(/(\*\*)\s*(:)/g, "$1\\$2").replace(/(\d+\.\s*)(@\{{3}[\s\S]{1,1024}?\}{3})/g, "$1$2").replace(/([-*]\s*)(@\{{3}[\s\S]{1,1024}?\}{3})/g, "$1$2").replace(/(\s+[-*]\s*)(@\{{3}[\s\S]{1,1024}?\}{3})/g, "$1$2").replace(/(\s+\d+\.\s*)(@\{{3}[\s\S]{1,1024}?\}{3})/g, "$1$2").replace(/(\d+\.\s*)\n/g, "$1\n").replace(/([-*]\s*)\n/g, "$1\n").replace(/\(([^)]{0,10000})\)/g, (match) => {
    return match.replace(/##/g, "\\#\\#");
  });
}
function extractTextFromReactNode(node) {
  if (typeof node === "string") return node;
  if (typeof node === "number") return node.toString();
  if (Array.isArray(node)) return node.map(extractTextFromReactNode).join("");
  if (isValidElement(node)) return extractTextFromReactNode(node.props.children);
  return "";
}
function isParentheticalDescription(text) {
  const trimmed = (text == null ? void 0 : text.trim()) || "";
  if (trimmed.startsWith("(") && trimmed.endsWith(")")) {
    return true;
  }
  const boldPattern = /^\*\*[^*]{0,1024}\*\*[:：]/;
  if (boldPattern.test(trimmed)) {
    return false;
  }
  const endsWithColon = /[:：]\s*$/.test(trimmed);
  if (endsWithColon) {
    return true;
  }
  return false;
}
function containsNestedList(node) {
  if (!node) return false;
  if (Array.isArray(node)) {
    return node.some((child) => containsNestedList(child));
  }
  if (isValidElement(node)) {
    const elementType = String(node.type);
    if (elementType === "ul" || elementType === "ol" || elementType.includes("ul") || elementType.includes("ol")) {
      return true;
    }
    return containsNestedList(node.props.children);
  }
  return false;
}
const INLINE_ELEMENTS = [
  "strong",
  "em",
  "b",
  "i",
  "u",
  "s",
  "mark",
  "small",
  "del",
  "ins",
  "code",
  "kbd",
  "samp",
  "var",
  "pre",
  "a",
  "abbr",
  "cite",
  "q",
  "dfn",
  "time",
  "data",
  "bdi",
  "bdo",
  "ruby",
  "rt",
  "rp",
  "span",
  "sub",
  "sup"
];
const MarkdownText = ({
  as: Component = "span",
  isHeading = false,
  emphasized = false,
  className,
  children,
  ...props
}) => {
  const baseClasses = isHeading ? "text-[14px] leading-[16px]" : "text-[14px] leading-[20px]";
  const fontWeight = emphasized ? "font-semibold" : "font-normal";
  return /* @__PURE__ */ jsx(
    Component,
    {
      className: cn("font-sans", baseClasses, fontWeight, className),
      ...props,
      children
    }
  );
};
const renderWithMentions = (text, onMentionClick, isHeading = false, isEmphasized = false, isCode = false, isList = false) => {
  if (!text || !text.includes("@{{{")) {
    return /* @__PURE__ */ jsx(Fragment, { children: text });
  }
  const regex = new RegExp(MENTION_REGEX.ENCODED, "g");
  const parts = [];
  let currentIndex = 0;
  let key = 0;
  let match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > currentIndex) {
      parts.push(
        /* @__PURE__ */ jsx("span", { className: "break-words", children: text.substring(currentIndex, match.index) }, key++)
      );
    }
    parts.push(
      /* @__PURE__ */ jsx(
        MentionFormatter,
        {
          text: match[0],
          className: isHeading ? "inline-block text-[13px] leading-[16px] font-semibold" : isEmphasized ? "inline-block text-[13px] leading-[20px] font-semibold" : isCode ? "inline-block text-[13px] leading-[20px] font-mono" : isList ? "inline-block text-[13px] leading-[20px]" : "inline-block text-[13px] leading-[20px]",
          onMentionClick,
          isHeading,
          isEmphasized,
          isCode,
          isList
        },
        key++
      )
    );
    currentIndex = match.index + match[0].length;
  }
  if (currentIndex < text.length) {
    parts.push(
      /* @__PURE__ */ jsx("span", { className: "break-words", children: text.substring(currentIndex) }, key++)
    );
  }
  return /* @__PURE__ */ jsx("span", { className: "inline break-words", children: parts });
};
function renderWithMentionsInNode(node, onMentionClick, contextProps = {}) {
  const {
    isHeading = false,
    isEmphasized = false,
    isCode = false,
    isList = false
  } = contextProps;
  if (typeof node === "string") {
    if (node.includes("@{{{")) {
      return renderWithMentions(
        node,
        onMentionClick,
        isHeading,
        isEmphasized,
        isCode,
        isList
      );
    }
    return node;
  }
  if (Array.isArray(node)) {
    return node.map((child, index) => /* @__PURE__ */ jsx(Fragment$1, { children: renderWithMentionsInNode(child, onMentionClick, contextProps) }, index));
  }
  if (isValidElement(node)) {
    let newContextProps = { ...contextProps };
    if (node.type === "strong" || node.type === "b") {
      newContextProps.isEmphasized = true;
    } else if (node.type === "code" || node.type === "pre") {
      newContextProps.isCode = true;
    } else if (node.type === "h1" || node.type === "h2" || node.type === "h3" || node.type === "h4" || node.type === "h5" || node.type === "h6") {
      newContextProps.isHeading = true;
    } else if (node.type === "li") {
      newContextProps.isList = true;
    }
    if (node.type === "ul" || node.type === "ol" || node.type === "li") {
      return {
        ...node,
        props: {
          ...node.props,
          // Carefully preserve the children structure for lists
          children: renderWithMentionsInNode(
            node.props.children,
            onMentionClick,
            newContextProps
          ),
          // Preserve any data attributes that help with nested list rendering
          "data-has-mentions": true
        }
      };
    }
    return {
      ...node,
      props: {
        ...node.props,
        children: renderWithMentionsInNode(
          node.props.children,
          onMentionClick,
          newContextProps
        )
      }
    };
  }
  return node;
}
function filterWhitespaceChildren(children) {
  const result = Children.toArray(children).filter((child) => {
    if (typeof child === "string" && (child.trim() === "" || child === "\n")) {
      return false;
    }
    if (isValidElement(child) && child.type === Fragment$1) {
      const fragmentChildren = Children.toArray(child.props.children);
      if (fragmentChildren.every(
        (fc) => typeof fc === "string" && (fc.trim() === "" || fc === "\n")
      )) {
        return false;
      }
      return true;
    }
    return true;
  });
  return result.map((child) => {
    if (isValidElement(child) && child.props.children) {
      return {
        ...child,
        props: {
          ...child.props,
          children: filterWhitespaceChildren(child.props.children)
        }
      };
    }
    return child;
  });
}
function isNestedList(node) {
  var _a, _b, _c;
  try {
    if (((_a = node == null ? void 0 : node.parent) == null ? void 0 : _a.tagName) === "li") {
      return true;
    }
    if (((_c = (_b = node == null ? void 0 : node.parent) == null ? void 0 : _b.parent) == null ? void 0 : _c.tagName) === "li") {
      return true;
    }
    return false;
  } catch (error) {
    return false;
  }
}
const getMarkdownComponents = (onMentionClick) => ({
  p: (props) => {
    const content = extractTextFromReactNode(props.children);
    const isReference = /^\[\d+\]$/.test(content);
    const isReferencesHeader = content.trim() === "References:";
    const isParenthetical = isParentheticalDescription(content);
    if (content.includes("@{{{")) {
      return /* @__PURE__ */ jsx(
        MarkdownText,
        {
          className: cn(
            isReference ? "inline ml-1" : "block",
            isReferencesHeader ? "block" : "",
            isParenthetical ? "inline" : ""
          ),
          children: renderWithMentions(
            content,
            onMentionClick,
            false,
            false,
            false,
            false
          )
        }
      );
    }
    return /* @__PURE__ */ jsx(
      MarkdownText,
      {
        className: cn(
          isReference ? "inline ml-1" : "block",
          isReferencesHeader ? "block" : "",
          isParenthetical ? "inline" : ""
        ),
        ...props
      }
    );
  },
  strong: (props) => {
    const content = extractTextFromReactNode(props.children);
    if (content.includes("@{{{")) {
      return /* @__PURE__ */ jsx(MarkdownText, { emphasized: true, children: renderWithMentions(
        content,
        onMentionClick,
        false,
        true,
        false,
        false
      ) });
    }
    return /* @__PURE__ */ jsx(MarkdownText, { emphasized: true, ...props });
  },
  li: (props) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l;
    const isReferenceItem = (_e = (_d = (_c = (_b = (_a = props.node) == null ? void 0 : _a.parent) == null ? void 0 : _b.parent) == null ? void 0 : _c.children) == null ? void 0 : _d.some) == null ? void 0 : _e.call(
      _d,
      (child) => {
        var _a2;
        return child.type === "text" && ((_a2 = child.value) == null ? void 0 : _a2.trim()) === "References:";
      }
    );
    const isParentWithNestedList = (_h = (_g = (_f = props.node) == null ? void 0 : _f.children) == null ? void 0 : _g.some) == null ? void 0 : _h.call(
      _g,
      (child) => child.type === "list"
    );
    const hasNestedElement = (_k = (_j = (_i = props.node) == null ? void 0 : _i.children) == null ? void 0 : _j.some) == null ? void 0 : _k.call(
      _j,
      (child) => child.type === "element" && child.tagName && (child.tagName === "ul" || child.tagName === "ol")
    );
    const hasNestedList = containsNestedList(props.children);
    const textContent = extractTextFromReactNode(props.children);
    const hasIndentation = textContent.includes("  -") || textContent.includes("   1");
    const hasBoldWithColon = textContent.includes("**") && /\*\*[^*]+\*\*\s*:/.test(textContent);
    let hasOnlyInlineElements = true;
    if ((_l = props.node) == null ? void 0 : _l.children) {
      hasOnlyInlineElements = props.node.children.every(
        (child) => {
          return child.type === "text" || child.type === "element" && INLINE_ELEMENTS.includes(child.tagName);
        }
      );
    }
    const useColumnLayout = (hasNestedList || isParentWithNestedList || hasIndentation || hasNestedElement || hasBoldWithColon) && !hasOnlyInlineElements;
    const cleanedChildren = filterWhitespaceChildren(props.children);
    const hasMentions = textContent.includes("@{{{");
    if (hasMentions) {
      if (useColumnLayout || hasNestedList || isParentWithNestedList) {
        return /* @__PURE__ */ jsx("li", { className: cn("list-item py-1.5", isReferenceItem ? "mb-0" : ""), children: /* @__PURE__ */ jsx("div", { className: "break-words flex flex-col", children: renderWithMentionsInNode(cleanedChildren, onMentionClick, {
          isList: true
        }) }) });
      }
      return /* @__PURE__ */ jsx("li", { className: cn("list-item py-1.5", isReferenceItem ? "mb-0" : ""), children: /* @__PURE__ */ jsx(
        "div",
        {
          className: cn(
            "break-words",
            "inline-flex flex-wrap items-baseline"
            // Original styling for non-nested content
          ),
          children: typeof textContent === "string" && textContent.includes("@{{{") ? renderWithMentions(
            textContent,
            onMentionClick,
            false,
            false,
            false,
            true
          ) : renderWithMentionsInNode(cleanedChildren, onMentionClick, {
            isList: true
          })
        }
      ) });
    }
    if (hasBoldWithColon && !useColumnLayout) {
      return /* @__PURE__ */ jsx("li", { className: cn("list-item py-1.5", isReferenceItem ? "mb-0" : ""), children: /* @__PURE__ */ jsx("div", { className: "break-words flex flex-col", children: renderWithMentionsInNode(cleanedChildren, onMentionClick, {
        isList: true
      }) }) });
    }
    return /* @__PURE__ */ jsx("li", { className: cn("list-item py-1.5", isReferenceItem ? "mb-0" : ""), children: /* @__PURE__ */ jsx(
      "div",
      {
        className: cn(
          "break-words",
          useColumnLayout ? "flex flex-col" : "inline-flex flex-wrap items-baseline"
          // Original styling for non-nested content
        ),
        children: renderWithMentionsInNode(cleanedChildren, onMentionClick, {
          isList: true
        })
      }
    ) });
  },
  h1: (props) => {
    const content = extractTextFromReactNode(props.children);
    if (isParentheticalDescription(content)) {
      return /* @__PURE__ */ jsx(MarkdownText, { className: "inline m-0", children: renderWithMentions(content, onMentionClick) });
    }
    if (content.includes("@{{{")) {
      return /* @__PURE__ */ jsx(MarkdownText, { as: "h1", isHeading: true, emphasized: true, className: "my-0", children: renderWithMentions(
        content,
        onMentionClick,
        true,
        false,
        false,
        false
      ) });
    }
    return /* @__PURE__ */ jsx(MarkdownText, { as: "h1", isHeading: true, emphasized: true, className: "my-0", ...props });
  },
  h2: (props) => {
    const content = extractTextFromReactNode(props.children);
    if (isParentheticalDescription(content)) {
      return /* @__PURE__ */ jsx(MarkdownText, { className: "inline m-0", children: renderWithMentions(content, onMentionClick) });
    }
    if (content.includes("@{{{")) {
      return /* @__PURE__ */ jsx(MarkdownText, { as: "h2", isHeading: true, emphasized: true, className: "my-0", children: renderWithMentions(
        content,
        onMentionClick,
        true,
        false,
        false,
        false
      ) });
    }
    return /* @__PURE__ */ jsx(MarkdownText, { as: "h2", isHeading: true, emphasized: true, className: "my-0", ...props });
  },
  h3: (props) => {
    const content = extractTextFromReactNode(props.children);
    if (isParentheticalDescription(content)) {
      return /* @__PURE__ */ jsx(MarkdownText, { className: "inline m-0", children: renderWithMentions(content, onMentionClick) });
    }
    if (content.includes("@{{{")) {
      return /* @__PURE__ */ jsx(MarkdownText, { as: "h3", isHeading: true, emphasized: true, className: "my-0", children: renderWithMentions(
        content,
        onMentionClick,
        true,
        false,
        false,
        false
      ) });
    }
    return /* @__PURE__ */ jsx(MarkdownText, { as: "h3", isHeading: true, emphasized: true, className: "my-0", ...props });
  },
  ul: (props) => {
    const { node, depth, ordered, ...rest } = props;
    const filteredChildren = filterWhitespaceChildren(props.children);
    const hasMention = extractTextFromReactNode(filteredChildren).includes("@{{{");
    const isNested = depth > 0 || isNestedList(node);
    const classNameString = cn(
      "agent-ui list-auto text-[14px] leading-[20px] marker:text-muted-foreground",
      "my-0 py-0",
      isNested ? "nested-list" : "pl-0",
      // Using nested-list class for CSS targeting
      hasMention ? "block" : ""
    );
    return /* @__PURE__ */ jsx(
      "ul",
      {
        className: classNameString,
        ...rest,
        "data-nested": isNested,
        "data-depth": depth,
        children: filteredChildren
      }
    );
  },
  ol: (props) => {
    const { node, depth, ordered, ...rest } = props;
    const filteredChildren = filterWhitespaceChildren(props.children);
    const hasMention = extractTextFromReactNode(filteredChildren).includes("@{{{");
    const isNested = depth > 0 || isNestedList(node);
    const classNameString = cn(
      "agent-ui list-decimal text-[14px] leading-[20px] marker:text-foreground",
      "my-0 py-0",
      isNested ? "nested-list" : "pl-0",
      // Using nested-list class for CSS targeting
      hasMention ? "block" : ""
    );
    return /* @__PURE__ */ jsx(
      "ol",
      {
        className: classNameString,
        ...rest,
        "data-nested": isNested,
        "data-depth": depth,
        children: filteredChildren
      }
    );
  },
  blockquote: ({ children, ...props }) => {
    const content = extractTextFromReactNode(children);
    if (content.includes("@{{{")) {
      return /* @__PURE__ */ jsx(
        "blockquote",
        {
          className: "border-l-4 border-primary pl-4 my-2 theme-static",
          ...props,
          children: /* @__PURE__ */ jsx(MarkdownText, { children: renderWithMentions(
            content,
            onMentionClick,
            false,
            false,
            false,
            false
          ) })
        }
      );
    }
    return /* @__PURE__ */ jsx(
      "blockquote",
      {
        className: "border-l-4 border-primary pl-4 my-2 theme-static",
        ...props,
        children: /* @__PURE__ */ jsx(MarkdownText, { children })
      }
    );
  },
  code: (props) => {
    const content = extractTextFromReactNode(props.children);
    if (content.includes("@{{{")) {
      return /* @__PURE__ */ jsx("code", { className: "px-1.5 py-0.5 bg-gray-100 rounded text-[14px] leading-[20px] font-mono break-words whitespace-pre-wrap max-w-full", children: renderWithMentions(
        content,
        onMentionClick,
        false,
        false,
        true,
        false
      ) });
    }
    return /* @__PURE__ */ jsx(
      "code",
      {
        className: "px-1.5 py-0.5 bg-gray-100 rounded text-[14px] leading-[20px] font-mono break-words whitespace-pre-wrap max-w-full",
        ...props
      }
    );
  }
});
const markdownComponents = getMarkdownComponents();
const SafeReactMarkdown = ({
  children,
  components,
  fallbackContent
}) => {
  try {
    return /* @__PURE__ */ jsx(ReactMarkdown, { components, children });
  } catch (error) {
    console.warn("Markdown rendering failed, using plain text:", error);
    return /* @__PURE__ */ jsx("div", { className: "plain-text whitespace-pre-wrap font-mono text-sm leading-relaxed", children: fallbackContent });
  }
};
const MixedContentBlock = ({
  content,
  isStreaming = false,
  components = markdownComponents,
  nextBlockType = "",
  prevBlockType = ""
}) => {
  const currentBlockType = getBlockType(content);
  if (currentBlockType === "table") {
    const processedContent = convertTableToCodeBlock(
      content.replace("<!--LIST_ITEM-->", "")
    );
    return /* @__PURE__ */ jsx("div", { className: "content-block table-as-code mt-2 mb-2", children: /* @__PURE__ */ jsx(SafeReactMarkdown, { components, fallbackContent: content, children: processedContent }) });
  }
  if (currentBlockType === "list") {
    const listContent = content.replace("<!--LIST_ITEM-->", "");
    let listMarginClass = "";
    if (prevBlockType !== "list") {
      listMarginClass += " mt-3";
    } else {
      listMarginClass += " mt-3";
    }
    if (nextBlockType !== "list") {
      listMarginClass += " mb-3";
    } else {
      listMarginClass += " mb-3";
    }
    if (listMarginClass === " mt-3 mb-3") listMarginClass = " my-3";
    else if (listMarginClass.trim() === "") listMarginClass = " my-0";
    const finalClass = `list-container ${listMarginClass}`;
    if (isStreaming) {
      const isCompleteListResult = isListComplete(listContent);
      if (!isCompleteListResult) {
        return /* @__PURE__ */ jsx("div", { className: finalClass, children: /* @__PURE__ */ jsx(
          SafeReactMarkdown,
          {
            components,
            fallbackContent: listContent,
            children: listContent
          }
        ) });
      }
    }
    if (blockContainsMentions(content)) {
      return /* @__PURE__ */ jsx("div", { className: finalClass, children: /* @__PURE__ */ jsx(
        SafeReactMarkdown,
        {
          components,
          fallbackContent: listContent,
          children: renderMixedContentWithMentions(listContent)
        }
      ) });
    }
    return /* @__PURE__ */ jsx("div", { className: finalClass, children: /* @__PURE__ */ jsx(
      SafeReactMarkdown,
      {
        components,
        fallbackContent: listContent,
        children: listContent
      }
    ) });
  }
  let marginClass = "";
  if (currentBlockType === "paragraph" || currentBlockType === "heading") {
    if (nextBlockType === "paragraph" || nextBlockType === "heading") {
      marginClass += " mb-2";
    } else if (prevBlockType === "paragraph" || prevBlockType === "heading") {
      if (prevBlockType) marginClass += " mt-2";
    } else if (nextBlockType && nextBlockType !== "list") {
      marginClass += " mb-2";
    }
  } else if (currentBlockType === "code") {
    if (prevBlockType !== "code") marginClass += " mt-2";
    if (nextBlockType !== "code") marginClass += " mb-2";
    if (marginClass === " mt-2 mb-2") marginClass = " my-2";
  }
  if (marginClass.trim() === "") {
    marginClass = " my-0";
  }
  const finalMarginClass = `content-block ${marginClass.trim()}`.trim();
  if (blockContainsMentions(content)) {
    return /* @__PURE__ */ jsx("div", { className: finalMarginClass, children: /* @__PURE__ */ jsx(SafeReactMarkdown, { components, fallbackContent: content, children: renderMixedContentWithMentions(content) }) });
  }
  return /* @__PURE__ */ jsx("div", { className: finalMarginClass, children: /* @__PURE__ */ jsx(SafeReactMarkdown, { components, fallbackContent: content, children: content }) });
};
function MarkdownWithMentionsComponent({
  content,
  isStreaming = false,
  className = "text-xs w-full",
  onMentionClick,
  components
}) {
  const cleanContent = useMemo(() => {
    return sanitizeMentions(content);
  }, [content]);
  const blocks = useMemo(() => {
    return parseMarkdownIntoBlocks(cleanContent);
  }, [cleanContent]);
  const mightHaveNestedLists = useMemo(() => {
    if (!isStreaming) return false;
    const nestedListPatterns = [/\n\s{2,10}[-*]/, /\n\s{2,10}\d{1,10}\./];
    return nestedListPatterns.some((pattern) => pattern.test(cleanContent));
  }, [cleanContent, isStreaming]);
  const customComponents = useMemo(() => {
    if (!onMentionClick) return components || markdownComponents;
    const baseComponents = components || markdownComponents;
    return {
      ...baseComponents,
      text: ({ children }) => {
        if (typeof children === "string" && children.includes("@{{{")) {
          return /* @__PURE__ */ jsx(MentionFormatter, { text: children, onMentionClick });
        }
        return /* @__PURE__ */ jsx(Fragment, { children });
      }
    };
  }, [onMentionClick, components]);
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cn(
        className,
        mightHaveNestedLists ? "nested-list-container" : "",
        isStreaming ? "is-streaming" : ""
      ),
      children: blocks.map((block, index) => {
        const key = generateBlockKey(block, index);
        const cleanedBlock = sanitizeIncompleteBlock(block);
        const prevBlock = index > 0 ? blocks[index - 1] : "";
        const nextBlock = index < blocks.length - 1 ? blocks[index + 1] : "";
        return /* @__PURE__ */ jsx(
          MixedContentBlock,
          {
            content: cleanedBlock,
            isStreaming,
            components: customComponents,
            prevBlockType: getBlockType(prevBlock),
            nextBlockType: getBlockType(nextBlock)
          },
          key
        );
      })
    }
  );
}
const MarkdownWithMentions = memo(MarkdownWithMentionsComponent);
MarkdownWithMentions.displayName = "MarkdownWithMentions";
const UserMessagePrimitive = ({
  content,
  className,
  children,
  onRollback,
  isStreaming = false,
  onMentionClick
}) => {
  const mdComponents = useMemo(() => {
    return onMentionClick ? getMarkdownComponents(onMentionClick) : getMarkdownComponents();
  }, [onMentionClick]);
  const renderContent = () => {
    const markdownContent = /* @__PURE__ */ jsx(
      MarkdownWithMentions,
      {
        content,
        isStreaming,
        onMentionClick,
        components: mdComponents
      }
    );
    return isStreaming ? markdownContent : /* @__PURE__ */ jsx(Suspense, { fallback: null, children: markdownContent });
  };
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "flex flex-row gap-2 items-center justify-start relative group max-w-[350px]",
        className
      ),
      children: [
        /* @__PURE__ */ jsx("div", { className: "rounded-sm py-2 px-4 whitespace-pre-wrap max-w-full w-fit bg-background-90 break-words theme-static", children: /* @__PURE__ */ jsx("div", { className: "overflow-hidden", children: children || renderContent() }) }),
        !!onRollback && /* @__PURE__ */ jsx("div", { className: "opacity-0 group-hover:opacity-100 transition-opacity duration-200", children: /* @__PURE__ */ jsx(
          Button,
          {
            onClick: onRollback,
            variant: "ghost",
            size: "icon-sm",
            "aria-label": "Rollback to this message",
            icon: "history",
            title: "Rollback to this message",
            className: "theme-static"
          }
        ) })
      ]
    }
  );
};
const MAX_CACHE_SIZE = 500;
const animatedContents = /* @__PURE__ */ new Set();
const animationTimestamps = /* @__PURE__ */ new Map();
function cleanupCache() {
  if (animatedContents.size <= MAX_CACHE_SIZE) return;
  const entries = Array.from(animationTimestamps.entries());
  entries.sort((a, b) => a[1] - b[1]);
  const entriesToRemove = entries.slice(0, entries.length - MAX_CACHE_SIZE);
  entriesToRemove.forEach(([key]) => {
    animatedContents.delete(key);
    animationTimestamps.delete(key);
  });
}
function useAnimatedText(text) {
  const [cursor, setCursor] = useState(0);
  const [startingCursor, setStartingCursor] = useState(0);
  const [prevText, setPrevText] = useState(text);
  const contentHash = useMemo(
    () => text.slice(0, 40).replace(/\s+/g, ""),
    [text]
  );
  const hasAnimated = animatedContents.has(contentHash);
  if (prevText !== text && !hasAnimated) {
    setPrevText(text);
    setStartingCursor(text.startsWith(prevText) ? cursor : 0);
  }
  useEffect(() => {
    if (hasAnimated) {
      setCursor(text.length);
      return;
    }
    const controls = animate(startingCursor, text.length, {
      duration: 0.3,
      ease: [0.6, 0.6, 0, 1],
      onUpdate(latest) {
        setCursor(Math.floor(latest));
      },
      onComplete() {
        animatedContents.add(contentHash);
        animationTimestamps.set(contentHash, Date.now());
        cleanupCache();
      }
    });
    return () => controls.stop();
  }, [startingCursor, text, hasAnimated, contentHash]);
  return hasAnimated ? text : text.substring(0, cursor);
}
const toTitleCase = (str) => {
  return str.split("_").map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(" ");
};
const transformContent = (content) => {
  content = content == null ? void 0 : content.replace(
    /<(\w{1,50})>([^<]{0,10000})<\/\1>/g,
    (_, tag, text) => {
      return `#### ${toTitleCase(tag)}
${text}

`;
    }
  );
  content = content.replace(/\[\[([^\]]{0,1000})\]\]/g, (_, text) => {
    return `[${text}](#field)`;
  });
  return content.trim();
};
const AssistantMessagePrimitive = ({
  content,
  className,
  children,
  isStreaming = false,
  onMentionClick
}) => {
  const mdComponents = useMemo(() => {
    return onMentionClick ? getMarkdownComponents(onMentionClick) : getMarkdownComponents();
  }, [onMentionClick]);
  const transformedContent = typeof content === "string" ? transformContent(content) : "";
  const animatedContent = useAnimatedText(transformedContent);
  const renderContent = () => {
    const markdownContent = /* @__PURE__ */ jsx(
      MarkdownWithMentions,
      {
        content: animatedContent,
        isStreaming,
        onMentionClick,
        components: mdComponents
      }
    );
    return isStreaming ? markdownContent : /* @__PURE__ */ jsx(Suspense, { fallback: null, children: markdownContent });
  };
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cn("flex items-start justify-start max-w-[325px]", className),
      children: /* @__PURE__ */ jsx("div", { className: "rounded-sm py-2 whitespace-pre-wrap max-w-full w-full bg-base-white text-sm [&>*:last-child]:mb-0 break-words theme-static", children: /* @__PURE__ */ jsx("div", { className: "overflow-hidden w-full", children: children || renderContent() }) })
    }
  );
};
const ToolLoadingIndicator = ({ title }) => {
  return /* @__PURE__ */ jsxs("div", { className: "w-full rounded-sm flex items-center gap-2 bg-background-90 p-2 justify-center theme-static", children: [
    /* @__PURE__ */ jsx(Spinner, { variant: "primary", size: 10 }),
    /* @__PURE__ */ jsx(Text, { className: "theme-static", children: title })
  ] });
};
const ToolDecisions = ({
  toolCallId,
  onToolDecision,
  content,
  isLoading
}) => {
  const handleDecision = (decision) => {
    onToolDecision == null ? void 0 : onToolDecision(toolCallId, decision);
  };
  if (isLoading) {
    return /* @__PURE__ */ jsx(
      ToolLoadingIndicator,
      {
        title: (content == null ? void 0 : content.applyingTool) ?? "Applying transformation"
      }
    );
  }
  return /* @__PURE__ */ jsxs("div", { className: "flex gap-2 w-full", children: [
    /* @__PURE__ */ jsx(
      Button,
      {
        className: "grow theme-static",
        variant: "secondary",
        onClick: () => handleDecision("rejected"),
        iconPlacement: "left",
        children: (content == null ? void 0 : content.reject) ?? "Reject"
      }
    ),
    /* @__PURE__ */ jsx(
      Button,
      {
        className: "grow theme-static",
        variant: "primary",
        onClick: () => handleDecision("accepted"),
        iconPlacement: "left",
        children: (content == null ? void 0 : content.approve) ?? "Accept"
      }
    )
  ] });
};
const ToolSaveButton = ({
  onSave,
  onOpen,
  onClose,
  isOpen,
  toolCallId,
  content,
  summaryText
}) => {
  const [saved, setSaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  let buttonLabel;
  let buttonIcon;
  let buttonAction;
  if (!saved) {
    buttonLabel = (content == null ? void 0 : content.save) ?? "Save";
    buttonIcon = "save";
    buttonAction = () => {
      setIsSaving(true);
      onSave == null ? void 0 : onSave(toolCallId).then(({ success }) => {
        if (success) {
          setSaved(true);
        }
        setIsSaving(false);
      });
    };
  } else if (isOpen && onClose) {
    buttonLabel = (content == null ? void 0 : content.close) ?? "Close";
    buttonIcon = "cross-small";
    buttonAction = () => onClose == null ? void 0 : onClose(toolCallId);
  } else {
    if (onOpen) {
      buttonLabel = (content == null ? void 0 : content.open) ?? "Open";
      buttonIcon = (content == null ? void 0 : content.openIcon) ?? "magic-wand-2";
      buttonAction = () => onOpen == null ? void 0 : onOpen(toolCallId);
    } else {
      buttonLabel = (content == null ? void 0 : content.saved) ?? "Saved";
      buttonIcon = "check";
      buttonAction = () => {
      };
    }
  }
  if (isSaving) {
    return /* @__PURE__ */ jsx(ToolLoadingIndicator, { title: (content == null ? void 0 : content.savingTool) ?? "Saving..." });
  }
  if (!saved && onSave || saved && (onOpen || onClose))
    return /* @__PURE__ */ jsxs("div", { className: "w-full space-y-2", children: [
      summaryText && /* @__PURE__ */ jsx(Text, { className: "text-sm text-muted-foreground theme-static", children: summaryText }),
      /* @__PURE__ */ jsx(
        Button,
        {
          className: "w-full theme-static",
          variant: "secondary",
          icon: buttonIcon,
          onClick: buttonAction,
          children: buttonLabel
        }
      )
    ] });
  return null;
};
const ToolCallActions = ({
  toolStatus,
  state,
  toolCallId,
  tool,
  tools
}) => {
  var _a;
  const { decision, loading } = toolStatus ?? {};
  if (!(tools == null ? void 0 : tools.onToolDecision) && !(tool == null ? void 0 : tool.decisionsEnabled) && !(tool == null ? void 0 : tool.saveEnabled) || state === "error" || state === "running" && !((_a = tools == null ? void 0 : tools.content) == null ? void 0 : _a.runningText) || decision === "rejected")
    return null;
  const isPendingDecision = (decision === "pending" || !decision) && (tool == null ? void 0 : tool.decisionsEnabled);
  if (isPendingDecision || loading)
    return /* @__PURE__ */ jsx(
      ToolDecisions,
      {
        toolCallId: toolCallId ?? "",
        onToolDecision: tools == null ? void 0 : tools.onToolDecision,
        content: tools == null ? void 0 : tools.content,
        isLoading: loading
      }
    );
  if (tool == null ? void 0 : tool.saveEnabled)
    return /* @__PURE__ */ jsx(
      ToolSaveButton,
      {
        toolCallId: toolCallId ?? "",
        onSave: tools == null ? void 0 : tools.onSave,
        onOpen: tools == null ? void 0 : tools.onOpen,
        onClose: tools == null ? void 0 : tools.onClose,
        isOpen: tools == null ? void 0 : tools.isOpen,
        content: tools == null ? void 0 : tools.content,
        summaryText: tool.summaryText
      }
    );
  return null;
};
const ToolCallQuickActionTray = ({
  onRevert,
  toolId,
  isLastMessage = false,
  onToolRollback
}) => {
  if (!toolId || !onRevert || isLastMessage) return null;
  const handleRollback = async () => {
    const result = await (onToolRollback == null ? void 0 : onToolRollback(toolId));
    if (result == null ? void 0 : result.success) {
      onRevert == null ? void 0 : onRevert();
    }
  };
  return /* @__PURE__ */ jsx("div", { className: "opacity-0 group-hover:opacity-100 transition-all duration-200 pt-1 mb-[-10px]", children: /* @__PURE__ */ jsx(
    Button,
    {
      onClick: handleRollback,
      variant: "ghost",
      size: "icon-sm",
      "aria-label": "Rollback to this tool call",
      icon: "history",
      title: "Rollback to this tool call",
      className: "theme-static"
    }
  ) });
};
const ToolCallPrimitive = ({
  state,
  message,
  toolStatus,
  toolInvotation,
  tools,
  onRevert,
  isLastMessage = false
}) => {
  var _a, _b, _c, _d, _e, _f;
  const tool = (_a = tools == null ? void 0 : tools.tools) == null ? void 0 : _a[(toolInvotation == null ? void 0 : toolInvotation.toolName) ?? ""];
  const { decision } = toolStatus ?? {};
  const textColor = decision === "rejected" ? "muted" : state === "error" ? "destructive" : "primary";
  return /* @__PURE__ */ jsxs("div", { className: "group", children: [
    /* @__PURE__ */ jsxs(
      "div",
      {
        className: cn(
          "border border-input rounded-[5px] p-3 text-sm flex items-center gap-3 flex-wrap w-full theme-static",
          { "border-destructive": state === "error" }
        ),
        children: [
          /* @__PURE__ */ jsx(
            PrimaryIcon,
            {
              state,
              decision,
              iconName: (tool == null ? void 0 : tool.iconName) || ((_b = toolInvotation == null ? void 0 : toolInvotation.args) == null ? void 0 : _b.iconName)
            }
          ),
          /* @__PURE__ */ jsx(
            Text,
            {
              className: cn(
                { "line-through": decision === "rejected" },
                "theme-static flex-1",
                {
                  "relative after:absolute after:inset-0 after:bg-gradient-to-r after:from-transparent after:via-white-opacity-4 after:to-transparent after:bg-[length:200%_100%] after:animate-shimmer after:pointer-events-none": state === "running"
                }
              ),
              color: textColor,
              children: decision === "rejected" && ((_c = tools == null ? void 0 : tools.content) == null ? void 0 : _c.rejectedText) ? tools.content.rejectedText : state === "running" && ((_d = tools == null ? void 0 : tools.content) == null ? void 0 : _d.runningText) ? tools.content.runningText : message
            }
          ),
          /* @__PURE__ */ jsx(
            ToolCallActions,
            {
              toolStatus,
              state,
              toolCallId: toolInvotation == null ? void 0 : toolInvotation.toolCallId,
              tool,
              tools
            }
          ),
          ((_e = toolInvotation == null ? void 0 : toolInvotation.args) == null ? void 0 : _e.showToolLoadingButton) && /* @__PURE__ */ jsx(
            ToolLoadingIndicator,
            {
              title: ((_f = toolInvotation == null ? void 0 : toolInvotation.args) == null ? void 0 : _f.toolLoadingButtonText) ?? "Generating Preview"
            }
          )
        ]
      }
    ),
    /* @__PURE__ */ jsx(
      ToolCallQuickActionTray,
      {
        onRevert,
        toolId: toolInvotation == null ? void 0 : toolInvotation.toolCallId,
        isLastMessage,
        onToolRollback: tools == null ? void 0 : tools.onToolRollback
      }
    )
  ] });
};
const PrimaryIcon = ({
  state,
  decision,
  iconName
}) => {
  const color = decision === "rejected" ? "gray-alpha-3" : state === "error" ? "destructive" : "primary";
  if (iconName) {
    return /* @__PURE__ */ jsx(Icon, { name: iconName, color, className: "theme-static" });
  }
  if (state === "success" && decision !== "rejected")
    return /* @__PURE__ */ jsx(
      Icon,
      {
        name: "status-checkmark-circle",
        className: "text-green-10 theme-static"
      }
    );
  if (decision === "rejected")
    return /* @__PURE__ */ jsx(
      Icon,
      {
        name: "status-x-circle",
        className: "text-gray-alpha-10 theme-static"
      }
    );
  if (!state) {
    return /* @__PURE__ */ jsx(
      Icon,
      {
        name: "status-half-circle",
        className: "text-gray-alpha-10 theme-static"
      }
    );
  }
  if (state === "running") {
    return /* @__PURE__ */ jsx(Spinner, { size: 10, className: "theme-static" });
  }
  return /* @__PURE__ */ jsx(
    Icon,
    {
      name: "status-warning-triangle",
      className: "text-destructive theme-static"
    }
  );
};
const SnapshotPrimitive = ({
  snapshotId,
  label,
  createdAt,
  onRollback,
  isRollbackDisabled = false,
  className
}) => {
  const createdAtDate = typeof createdAt === "string" ? new Date(createdAt) : createdAt;
  const timestamp = createdAtDate.toLocaleTimeString();
  return /* @__PURE__ */ jsx("div", { className: `flex items-start gap-2 w-full ${className || ""}`, children: /* @__PURE__ */ jsxs("div", { className: "flex flex-col justify-center items-start p-3 gap-2 bg-none rounded-[5px] border border-input w-full theme-static", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 text-sm text-foreground-80 theme-static", children: [
      /* @__PURE__ */ jsx(Icon, { name: "commit", className: "text-foreground-80 theme-static" }),
      "Checkpoint made · ",
      timestamp
    ] }),
    /* @__PURE__ */ jsx("span", { className: "text-14 theme-static", children: label }),
    /* @__PURE__ */ jsx(
      Button,
      {
        icon: "refresh",
        variant: "outline",
        className: "w-full theme-static",
        disabled: isRollbackDisabled,
        onClick: () => onRollback(snapshotId, createdAtDate.toISOString()),
        children: "Rollback to here"
      }
    )
  ] }) });
};
const ReasoningPrimitive = ({
  content,
  isComplete,
  className
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const toggleExpand = () => {
    setIsExpanded((prev) => !prev);
  };
  return /* @__PURE__ */ jsx("div", { className: `flex items-start gap-2 ${className || ""}`, children: /* @__PURE__ */ jsx("div", { className: "rounded-sm py-2 px-0 whitespace-pre-wrap max-w-full w-fit bg-base-white prose prose-sm break-words", children: /* @__PURE__ */ jsxs("div", { className: "overflow-hidden", children: [
    isComplete ? /* @__PURE__ */ jsxs(
      Button,
      {
        variant: "ghost",
        size: "sm",
        onClick: toggleExpand,
        className: "mb-2 h-6 min-h-[24px] p-0",
        children: [
          /* @__PURE__ */ jsx("span", { className: "text-sm", children: "Show reasoning" }),
          /* @__PURE__ */ jsx(
            Icon,
            {
              name: isExpanded ? "chevron-bottom" : "chevron-right",
              size: 12
            }
          )
        ]
      }
    ) : /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 text-sm mb-2 h-6 min-h-[24px]", children: [
      /* @__PURE__ */ jsx(Spinner, { size: 8 }),
      /* @__PURE__ */ jsx("span", { className: "break-normal", children: "Thinking..." })
    ] }),
    isExpanded && isComplete && /* @__PURE__ */ jsx("pre", { className: "p-4 bg-gray-100 rounded-sm w-full whitespace-pre-wrap font-mono text-xs mb-2 overflow-hidden break-words", children: content })
  ] }) }) });
};
const FileAttachmentPrimitive = ({
  fileName,
  isUserMessage = false,
  className
}) => {
  return /* @__PURE__ */ jsx("div", { className: cn("flex items-start gap-2 justify-start", className), children: /* @__PURE__ */ jsx(
    "div",
    {
      className: cn(
        "rounded-lg py-2 px-4 !pl-3 whitespace-pre-wrap max-w-full w-fit break-normal",
        isUserMessage ? "bg-background-90" : "bg-base-white"
      ),
      children: /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
        /* @__PURE__ */ jsx(Icon, { name: "file-bend", size: 16, className: "p-0" }),
        /* @__PURE__ */ jsx("span", { className: "text-base", children: fileName })
      ] })
    }
  ) });
};
const ErrorMessagePrimitive = ({
  message,
  className
}) => {
  return /* @__PURE__ */ jsx("div", { className: `flex items-center w-fit max-w-full ${className || ""}`, children: /* @__PURE__ */ jsxs("div", { className: "border border-input rounded-[5px] px-3 py-2 text-base w-fit max-w-full flex items-center gap-2", children: [
    /* @__PURE__ */ jsx(Icon, { name: "status-warning-triangle", className: "text-destructive" }),
    /* @__PURE__ */ jsx("span", { children: message })
  ] }) });
};
const ChatStatusPrimitive = ({
  statusType = "online",
  text,
  icon,
  actions,
  isVisible,
  isLoading
}) => {
  const [shouldRender, setShouldRender] = useState(false);
  const [height, setHeight] = useState("0px");
  const [opacity, setOpacity] = useState(0);
  const shouldBeVisible = isVisible !== void 0 ? isVisible : Boolean(text);
  useEffect(() => {
    if (shouldBeVisible) {
      setShouldRender(true);
      requestAnimationFrame(() => {
        setTimeout(() => {
          setHeight("32px");
          setOpacity(1);
        }, 30);
      });
    } else {
      setHeight("0px");
      setOpacity(0);
      const hideTimer = setTimeout(() => {
        setShouldRender(false);
      }, 320);
      return () => clearTimeout(hideTimer);
    }
  }, [shouldBeVisible]);
  if (!shouldRender && !shouldBeVisible) {
    return /* @__PURE__ */ jsx(Fragment, {});
  }
  const getDefaultIcon = () => {
    switch (statusType) {
      case "loading":
        return /* @__PURE__ */ jsx(Spinner, { size: 8, className: "flex-shrink-0 theme-static" });
      case "error":
        return /* @__PURE__ */ jsx(
          Icon,
          {
            name: "status-warning-triangle",
            className: "text-destructive flex-shrink-0 theme-static"
          }
        );
      case "paused":
        return /* @__PURE__ */ jsx(
          Icon,
          {
            name: "pause",
            className: "text-foreground-50 flex-shrink-0 theme-static"
          }
        );
      case "online":
        return null;
      case "custom":
      default:
        return null;
    }
  };
  const displayIcon = icon || getDefaultIcon();
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "flex items-center justify-between px-4 bg-background-80 transition-all duration-300 ease-in-out overflow-hidden border-b border-primary-dark theme-static",
        { "bg-primary theme-static": isLoading }
      ),
      style: {
        height,
        opacity,
        transformOrigin: "top",
        // Add will-change to help browser optimize the animation
        willChange: "height, opacity"
      },
      children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 flex-1 min-w-0", children: [
          displayIcon,
          /* @__PURE__ */ jsx(
            Text,
            {
              className: cn(
                "truncate theme-static grow",
                isLoading ? "text-white-opacity-1" : "text-primary"
              ),
              children: text
            }
          )
        ] }),
        /* @__PURE__ */ jsx("div", { className: "flex items-center gap-2 flex-shrink-0 ml-2", children: actions })
      ]
    }
  );
};
const ChatMessagePrimitive = ({
  children,
  isRolledBack = false,
  className
}) => {
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cn(
        "flex flex-col gap-2 w-full overflow-x-hidden",
        isRolledBack && "opacity-50",
        className
      ),
      children
    }
  );
};
const OptionsPrimitive = ({
  options,
  selectedOption,
  onOptionSelect,
  isLastMessage = false,
  text,
  messageId,
  reroll
}) => {
  const handleOptionClick = (option) => {
    const optionWithMessageId = {
      ...option,
      messageId
    };
    if (onOptionSelect) {
      onOptionSelect(optionWithMessageId);
    } else {
      console.warn("onOptionSelect is not defined");
    }
  };
  const isSelected = (option) => {
    return selectedOption === option.id;
  };
  const mdComponents = getMarkdownComponents();
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2 w-full max-w-full theme-static", children: [
    text && /* @__PURE__ */ jsx("div", { className: "w-full mb-2 theme-static", children: /* @__PURE__ */ jsx(
      MarkdownWithMentions,
      {
        content: text,
        isStreaming: false,
        components: mdComponents
      }
    ) }),
    /* @__PURE__ */ jsx("div", { className: "flex flex-col gap-1 w-full", children: options.filter((option) => !selectedOption || isSelected(option)).map((option, index) => {
      const isDisabled = option.disabled === true || !isLastMessage;
      const isSelected2 = option.disabled === true && options.filter((o) => o.disabled === true).length === 1;
      return /* @__PURE__ */ jsxs(
        "button",
        {
          className: `flex items-center text-left px-3 py-[6px] text-sm rounded-[5px] w-full theme-static ${isSelected2 ? "bg-gray-4" : isDisabled ? "text-gray-400 bg-gray-100 border border-gray-200 cursor-not-allowed" : "text-gray-900 bg-background-90 border border-gray-200 hover:bg-gray-4"}`,
          onClick: isDisabled ? void 0 : () => handleOptionClick(option),
          disabled: isDisabled,
          "aria-selected": isSelected2,
          children: [
            /* @__PURE__ */ jsx(
              Text,
              {
                variant: "callout",
                className: "mr-3 font-mono font-light text-muted theme-static",
                children: index + 1
              }
            ),
            /* @__PURE__ */ jsx(Text, { variant: "body", color: "primary", className: "theme-static", children: option.label || option.toString() }),
            isSelected2 && /* @__PURE__ */ jsx("span", { className: "ml-auto theme-static", children: /* @__PURE__ */ jsx(
              Icon,
              {
                name: "checkmark-2",
                className: "h-5 w-5 text-blue-600 dark:text-blue-400"
              }
            ) })
          ]
        },
        index
      );
    }) }),
    reroll && isLastMessage && messageId && /* @__PURE__ */ jsxs(
      "button",
      {
        onClick: () => reroll(messageId),
        className: "flex items-center text-left px-2 py-2 text-sm font-medium rounded-lg justify-center gap-2",
        children: [
          /* @__PURE__ */ jsx(Icon, { name: "refresh", className: "h-5 w-5" }),
          " Load More"
        ]
      }
    )
  ] });
};
const SheetAnalysisPrimitive = ({
  content
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const title = "Initial Sheet Analysis";
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("div", { className: "flex items-center justify-start gap-2 relative group", children: /* @__PURE__ */ jsxs(
      "div",
      {
        className: "text-left flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-md cursor-pointer hover:bg-gray-200 text-sm",
        onClick: () => setIsOpen(true),
        children: [
          /* @__PURE__ */ jsx(Icon, { name: "file-text" }),
          title
        ]
      }
    ) }),
    /* @__PURE__ */ jsx(Dialog, { open: isOpen, onOpenChange: setIsOpen, children: /* @__PURE__ */ jsxs(DialogContent, { className: "w-[60vw] max-w-[60vw]", children: [
      /* @__PURE__ */ jsx(DialogHeader, { children: /* @__PURE__ */ jsx(DialogTitle, { children: title }) }),
      /* @__PURE__ */ jsx("div", { className: "max-h-[60vh] max-w-[80vw] overflow-y-auto", children: /* @__PURE__ */ jsx(Suspense, { fallback: /* @__PURE__ */ jsx("div", { className: "p-4", children: "Loading content..." }), children: /* @__PURE__ */ jsx(MarkdownWithMentions, { content }) }) }),
      /* @__PURE__ */ jsx(DialogFooter, { children: /* @__PURE__ */ jsx(DialogCloseButton, {}) })
    ] }) })
  ] });
};
const StatusMessage = ({
  statuses,
  previewLength = 100,
  expandLatestByDefault = false
}) => {
  const [expandedItems, setExpandedItems] = useState(/* @__PURE__ */ new Set());
  if (!statuses || statuses.length === 0) return null;
  const latestStatus = statuses[statuses.length - 1];
  const toggleExpanded = (statusId) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(statusId)) {
      newExpanded.delete(statusId);
    } else {
      newExpanded.add(statusId);
    }
    setExpandedItems(newExpanded);
  };
  const expandAll = () => {
    const allIds = new Set(statuses.map((status) => status.id));
    setExpandedItems(allIds);
  };
  const collapseAll = () => {
    setExpandedItems(/* @__PURE__ */ new Set());
  };
  const getOverallStatusIcon = (status) => {
    switch (status) {
      case "running":
        return /* @__PURE__ */ jsx("svg", { className: "w-3 h-3 animate-spin", viewBox: "0 0 12 12", children: /* @__PURE__ */ jsx(
          "circle",
          {
            cx: "6",
            cy: "6",
            r: "5",
            fill: "none",
            stroke: "currentColor",
            strokeWidth: "2",
            strokeLinecap: "round",
            strokeDasharray: "28",
            className: "text-gray-600",
            style: { transform: "rotate(-90deg)", transformOrigin: "center" }
          }
        ) });
      case "completed":
        return /* @__PURE__ */ jsx(
          Icon,
          {
            name: "circle-check",
            size: 16,
            className: "text-gray-600",
            style: { width: "0.75rem", height: "0.75rem" }
          }
        );
      case "failed":
        return /* @__PURE__ */ jsx(
          Icon,
          {
            name: "circle-x",
            size: 16,
            className: "text-red-500",
            style: { width: "0.75rem", height: "0.75rem" }
          }
        );
      default:
        return /* @__PURE__ */ jsx(
          Icon,
          {
            name: "circle-info",
            size: 16,
            className: "text-gray-400",
            style: { width: "0.75rem", height: "0.75rem" }
          }
        );
    }
  };
  const getOpacity = (index, totalCount) => {
    const position = totalCount - index;
    if (position === 1) return "opacity-100";
    if (position === 2) return "opacity-80";
    if (position === 3) return "opacity-60";
    return "opacity-40";
  };
  const isLatest = (index, totalCount) => {
    return index === totalCount - 1;
  };
  const isExpanded = (statusId, index, totalCount) => {
    return expandLatestByDefault && isLatest(index, totalCount) || expandedItems.has(statusId);
  };
  const getTruncatedMessage = (message, maxLength) => {
    if (message.length <= maxLength) return message;
    const truncated = message.substring(0, maxLength);
    const lastSpace = truncated.lastIndexOf(" ");
    if (lastSpace > maxLength * 0.8) {
      return truncated.substring(0, lastSpace) + "...";
    }
    return truncated + "...";
  };
  const hasExpandableMessages = statuses.some(
    (status, index) => status.message.length > previewLength && (!expandLatestByDefault || !isLatest(index, statuses.length))
  );
  const getCircleIndicator = (status) => {
    switch (status) {
      case "completed":
        return /* @__PURE__ */ jsx("div", { className: "w-3 h-3 rounded-full bg-gray-600 flex items-center justify-center", children: /* @__PURE__ */ jsx(
          Icon,
          {
            name: "checkmark-2",
            size: 8,
            className: "text-white",
            style: { color: "white", fontSize: 8 }
          }
        ) });
      case "failed":
        return /* @__PURE__ */ jsx("div", { className: "w-3 h-3 rounded-full bg-red-500" });
      case "running":
        return /* @__PURE__ */ jsxs("div", { className: "relative", children: [
          /* @__PURE__ */ jsx("div", { className: "w-3 h-3 rounded-full bg-gray-600" }),
          /* @__PURE__ */ jsx("div", { className: "absolute inset-0 w-3 h-3 rounded-full border border-gray-300 animate-pulse" })
        ] });
      default:
        return /* @__PURE__ */ jsx("div", { className: "w-3 h-3 rounded-full bg-gray-400" });
    }
  };
  return /* @__PURE__ */ jsxs("div", { className: "border border-gray-200 rounded-lg p-4 mb-3 bg-gray-100", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3 mb-4", children: [
      /* @__PURE__ */ jsx("div", { className: "flex-shrink-0", children: getOverallStatusIcon(latestStatus.status) }),
      /* @__PURE__ */ jsx("div", { className: "flex-1", children: /* @__PURE__ */ jsx("span", { className: "text-sm font-medium text-gray-800", children: latestStatus.agentName || "Agent" }) }),
      hasExpandableMessages && statuses.length > 1 && /* @__PURE__ */ jsx(
        "button",
        {
          onClick: expandedItems.size === statuses.length ? collapseAll : expandAll,
          className: "text-xs text-gray-500 hover:text-gray-700 transition-colors",
          children: expandedItems.size === statuses.length ? "Collapse All" : "Expand All"
        }
      )
    ] }),
    /* @__PURE__ */ jsx("div", { className: "relative", children: statuses.map((status, index) => {
      const isLast = index === statuses.length - 1;
      const isExpandedState = isExpanded(status.id, index, statuses.length);
      const hasError = status.status === "failed";
      return /* @__PURE__ */ jsxs("div", { className: "relative", children: [
        !isLast && /* @__PURE__ */ jsx("div", { className: "absolute left-1.5 top-6 w-px h-6 bg-gray-200" }),
        /* @__PURE__ */ jsxs(
          "div",
          {
            className: `flex items-start gap-3 pb-4 ${getOpacity(
              index,
              statuses.length
            )}`,
            children: [
              /* @__PURE__ */ jsx("div", { className: "flex-shrink-0 relative mt-0.5", children: getCircleIndicator(status.status) }),
              /* @__PURE__ */ jsxs("div", { className: "flex-1 min-w-0", children: [
                /* @__PURE__ */ jsxs(
                  "button",
                  {
                    onClick: () => toggleExpanded(status.id),
                    className: "w-full text-left flex items-center gap-2 hover:bg-gray-50 -mx-2 px-2 py-1 rounded transition-colors group",
                    disabled: expandLatestByDefault && isLatest(index, statuses.length),
                    children: [
                      (!expandLatestByDefault || !isLatest(index, statuses.length)) && /* @__PURE__ */ jsx("div", { className: "flex-shrink-0 transition-transform duration-200", children: /* @__PURE__ */ jsx(
                        Icon,
                        {
                          name: isExpandedState ? "chevron-bottom" : "chevron-right",
                          size: 12,
                          className: "text-gray-400 group-hover:text-gray-600"
                        }
                      ) }),
                      /* @__PURE__ */ jsx("div", { className: "flex-1", children: /* @__PURE__ */ jsx(
                        "p",
                        {
                          className: `text-sm ${isLatest(index, statuses.length) ? "text-gray-900 font-medium" : "text-gray-700"} ${hasError ? "text-red-800" : ""}`,
                          children: !isExpandedState ? getTruncatedMessage(status.message, previewLength) : status.message.length <= previewLength ? status.message : getTruncatedMessage(
                            status.message,
                            previewLength
                          ) + "..."
                        }
                      ) })
                    ]
                  }
                ),
                isExpandedState && /* @__PURE__ */ jsxs("div", { className: "mt-2 pl-5 transition-all duration-200 ease-in-out", children: [
                  status.message.length > previewLength && /* @__PURE__ */ jsx(
                    "p",
                    {
                      className: `text-sm mb-2 ${hasError ? "text-red-700" : "text-gray-600"}`,
                      children: status.message
                    }
                  ),
                  /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between text-xs text-gray-400", children: [
                    /* @__PURE__ */ jsx("span", { children: status.stage }),
                    /* @__PURE__ */ jsx("span", { children: new Date(status.timestamp).toLocaleTimeString() })
                  ] })
                ] })
              ] })
            ]
          }
        )
      ] }, status.id + index);
    }) })
  ] });
};
const isReasoningPart = (part) => {
  return part.type === "reasoning";
};
const isTextPart = (part) => {
  return part.type === "text";
};
const isToolInvocationPart = (part) => {
  return part.type === "tool-invocation";
};
const isDataVizPart = (part) => {
  return part.type === "viz";
};
const ChatMessage = ({
  workbooks,
  onRevert,
  messageIndex,
  displayBoundary,
  startOrder,
  isRolledBack = false,
  isStreaming = false,
  onOptionSelect,
  onMentionClick,
  isLastMessage = false,
  reroll,
  toolRenderer,
  tools,
  statusMessagePreviewLength,
  ...message
}) => {
  var _a, _b, _c;
  const { reasoning, isReasoningComplete } = useMemo(() => {
    var _a2, _b2, _c2, _d;
    if (message.parts) {
      const reasoningPart = message.parts.find(isReasoningPart);
      const hasSignature = ((_b2 = (_a2 = reasoningPart == null ? void 0 : reasoningPart.details) == null ? void 0 : _a2[0]) == null ? void 0 : _b2.type) === "text" && ((_d = (_c2 = reasoningPart == null ? void 0 : reasoningPart.details) == null ? void 0 : _c2[0]) == null ? void 0 : _d.signature) !== void 0;
      return {
        reasoning: reasoningPart == null ? void 0 : reasoningPart.reasoning,
        isReasoningComplete: hasSignature
      };
    }
    return {
      reasoning: message.reasoning,
      isReasoningComplete: true
      // Default to true if no parts structure
    };
  }, [message.parts, message.reasoning]);
  if (((_a = message.data) == null ? void 0 : _a.messageType) === "error") {
    return /* @__PURE__ */ jsx(ErrorMessagePrimitive, { message: message.content || "Unknown error" });
  }
  const repressedTools = [
    "updateWorkbook",
    "loadBlueprint",
    "ui-action",
    "chartPreview"
  ];
  const toolStatusAnnotation = ((_b = message.annotations) == null ? void 0 : _b.find(
    (annotation) => typeof annotation === "object" && annotation !== null && "type" in annotation && annotation.type === "tool-statuses"
  )) || {};
  const toolStatusMap = toolStatusAnnotation.toolStatuses || {};
  const snapshots = ((_c = message.annotations) == null ? void 0 : _c.filter(
    (annotation) => typeof annotation === "object" && annotation !== null && "type" in annotation && annotation.type === "snapshot"
  )) || [];
  const snapshotMap = /* @__PURE__ */ new Map();
  snapshots.forEach((snapshot) => {
    if (snapshot.snapshot.toolCallId) {
      snapshotMap.set(snapshot.snapshot.toolCallId, snapshot);
    }
  });
  const renderParts = [];
  let orderCounter = 0;
  const renderToolWithSnapshot = (tool, order) => {
    var _a2, _b2;
    const elements = [];
    const snapshot = snapshotMap.get(tool.toolCallId);
    if (snapshot) {
      elements.push({
        key: `snapshot-${tool.toolCallId}`,
        order: order - 0.5,
        // Place just before the tool
        element: /* @__PURE__ */ jsx(
          SnapshotPrimitive,
          {
            snapshotId: snapshot.snapshot.id,
            label: snapshot.snapshot.label,
            createdAt: snapshot.snapshot.createdAt,
            isRollbackDisabled: isRolledBack,
            onRollback: (snapshotId, snapshotTime) => {
              const boundary = order;
              if (onRevert) {
                onRevert({
                  snapshotId,
                  snapshotTime,
                  messageIndex,
                  partOrder: boundary,
                  toolCallId: tool.toolCallId
                });
              }
            }
          },
          `snapshot-${tool.toolCallId}`
        )
      });
    }
    const isCancelled = isToolCancelled(tool);
    const toolState = isToolRunning(tool.state) ? "running" : isCancelled ? "error" : tool.state === "result" && ((_b2 = (_a2 = tool.result) == null ? void 0 : _a2.status) == null ? void 0 : _b2.toLowerCase()) === "success" ? "success" : "error";
    const toolMessage = getToolMessage(
      tool,
      isCancelled ? "cancelled" : toolState === "running" ? "running" : toolState === "success" ? "completed" : "failed",
      workbooks
    );
    elements.push({
      key: `tool-${tool.toolCallId}`,
      order,
      element: /* @__PURE__ */ jsx(
        ToolCallPrimitive,
        {
          state: toolState,
          message: toolMessage,
          toolInvotation: tool,
          toolStatus: toolStatusMap == null ? void 0 : toolStatusMap[tool.toolCallId],
          tools,
          onRevert: onRevert && !isLastMessage ? () => {
            var _a3;
            const boundary = ((_a3 = message.parts) == null ? void 0 : _a3.length) || order;
            onRevert({
              messageIndex,
              partOrder: boundary,
              toolCallId: tool.toolCallId
            });
          } : void 0
        },
        `tool-${tool.toolCallId}`
      )
    });
    return elements;
  };
  const renderReasoningElement = (order) => {
    if (!reasoning) return null;
    return {
      key: `reasoning-${order}`,
      order,
      element: /* @__PURE__ */ jsx(
        ReasoningPrimitive,
        {
          content: reasoning,
          isComplete: isReasoningComplete
        }
      )
    };
  };
  const renderTextElement = (text, order) => {
    return {
      key: `text-${order}`,
      order,
      element: message.role === "user" ? /* @__PURE__ */ jsx(
        UserMessagePrimitive,
        {
          content: text,
          isStreaming,
          onRollback: onRevert && message.createdAt && !isLastMessage ? () => {
            const snapshotTime = new Date(
              String(message.createdAt)
            ).toISOString();
            onRevert({
              snapshotTime,
              messageIndex: messageIndex + 1,
              partOrder: 0,
              toolCallId: void 0
            });
          } : void 0,
          onMentionClick
        }
      ) : /* @__PURE__ */ jsx(
        AssistantMessagePrimitive,
        {
          content: text,
          isStreaming,
          onMentionClick
        }
      )
    };
  };
  const experimentalAttachments = message.experimental_attachments || [];
  if (experimentalAttachments.length > 0) {
    experimentalAttachments.forEach((attachment, index) => {
      renderParts.push({
        key: `attachment-${index}`,
        order: orderCounter++,
        element: /* @__PURE__ */ jsx(
          FileAttachmentPrimitive,
          {
            fileName: attachment.name || `File ${index + 1}`,
            isUserMessage: message.role === "user"
          }
        )
      });
    });
  }
  const renderOptionsElement = (message2, order) => {
    return {
      key: `options-${order}`,
      order,
      element: /* @__PURE__ */ jsx(AssistantMessagePrimitive, { content: "", children: /* @__PURE__ */ jsx(
        OptionsPrimitive,
        {
          options: message2.options,
          selectedOption: message2.content && typeof message2.content === "object" ? message2.content.selected : void 0,
          onOptionSelect,
          isLastMessage,
          text: message2.text,
          messageId: message2.id,
          reroll
        }
      ) })
    };
  };
  const renderVizElement = (data, order) => {
    return {
      key: `viz-${order}`,
      order,
      element: /* @__PURE__ */ jsx(DataViz, { data })
    };
  };
  const renderSheetAnalysisElement = (content, order) => {
    return {
      key: `sheet-analysis-${order}`,
      order,
      element: /* @__PURE__ */ jsx(SheetAnalysisPrimitive, { content })
    };
  };
  const renderStatusElement = (message2, order) => {
    return {
      key: `status-${order}`,
      order,
      element: /* @__PURE__ */ jsx(
        StatusMessage,
        {
          statuses: message2.statuses || [],
          previewLength: statusMessagePreviewLength
        }
      )
    };
  };
  const messageParts = message.parts || [];
  if (messageParts.length > 0) {
    messageParts.forEach((part) => {
      const currentOrder = orderCounter++;
      if (isReasoningPart(part)) {
        const reasoningElement = renderReasoningElement(currentOrder);
        if (reasoningElement) {
          renderParts.push(reasoningElement);
        }
      } else if (isTextPart(part)) {
        renderParts.push(renderTextElement(part.text, currentOrder));
      } else if (isToolInvocationPart(part)) {
        const toolInvocation = part.toolInvocation;
        if ((toolInvocation == null ? void 0 : toolInvocation.toolName) === "chartPreview" && toolInvocation.state === "result") {
          const { chartType, data, config, dataKey, nameKey, title } = toolInvocation.result || {};
          const ChartMap = {
            bar: BarChartComponent,
            pie: PieChartComponent,
            area: AreaChartComponent
          };
          const Chart = ChartMap[chartType];
          if (Chart) {
            renderParts.push({
              key: `chart-${currentOrder}`,
              order: currentOrder,
              element: /* @__PURE__ */ jsx(
                Chart,
                {
                  data,
                  config,
                  dataKey,
                  nameKey,
                  title
                }
              )
            });
            return;
          }
        }
        const toolElement = toolRenderer == null ? void 0 : toolRenderer(toolInvocation, messageIndex);
        if (toolElement) {
          renderParts.push({
            key: `tool-${currentOrder}`,
            order: currentOrder,
            element: toolElement
          });
        } else if (toolInvocation && !repressedTools.includes(toolInvocation.toolName)) {
          const toolElements = renderToolWithSnapshot(
            toolInvocation,
            currentOrder
          );
          renderParts.push(...toolElements);
        }
      } else if (isDataVizPart(part)) {
        renderParts.push(renderVizElement(part.data, currentOrder));
      }
    });
  } else if (message.type === "options") {
    renderParts.push(renderOptionsElement(message, orderCounter++));
  } else if (message.type === "viz") {
    renderParts.push(renderVizElement(message.viz, orderCounter++));
  } else if (message.type === "sheet-analysis") {
    const messageContent = message.content;
    renderParts.push(renderSheetAnalysisElement(messageContent, orderCounter++));
  } else if (message.type === "status") {
    renderParts.push(renderStatusElement(message, orderCounter++));
  } else if (message.content) {
    renderParts.push(renderTextElement(message.content, orderCounter++));
  }
  const pairedToolCallIds = /* @__PURE__ */ new Set();
  if (message.parts) {
    message.parts.forEach((part) => {
      var _a2;
      if (part.type === "tool-invocation" && ((_a2 = part.toolInvocation) == null ? void 0 : _a2.toolCallId)) {
        pairedToolCallIds.add(part.toolInvocation.toolCallId);
      }
    });
  }
  const unpairedSnapshots = snapshots.filter((snapshot) => {
    if (snapshot.snapshot.toolCallId) {
      return !pairedToolCallIds.has(snapshot.snapshot.toolCallId);
    }
    return true;
  });
  unpairedSnapshots.forEach((snapshot, i) => {
    const currentOrder = orderCounter++;
    renderParts.push({
      key: `unpaired-snapshot-${i}`,
      order: currentOrder,
      element: /* @__PURE__ */ jsx(
        SnapshotPrimitive,
        {
          snapshotId: snapshot.snapshot.id,
          label: snapshot.snapshot.label,
          createdAt: snapshot.snapshot.createdAt,
          isRollbackDisabled: isRolledBack,
          onRollback: (snapshotId, snapshotTime) => {
            const boundary = currentOrder + 1;
            if (onRevert) {
              onRevert({
                snapshotId,
                snapshotTime,
                messageIndex,
                partOrder: boundary
              });
            }
          }
        },
        `unpaired-snapshot-${i}`
      )
    });
  });
  const filteredParts = renderParts.filter((part) => {
    if (displayBoundary !== void 0 && part.order >= displayBoundary)
      return false;
    if (startOrder !== void 0 && part.order < startOrder) return false;
    return true;
  });
  filteredParts.sort((a, b) => a.order - b.order);
  return /* @__PURE__ */ jsx(ChatMessagePrimitive, { isRolledBack, children: filteredParts.map((part) => /* @__PURE__ */ jsx(React.Fragment, { children: part.element }, part.key)) });
};
const BulkDecisionActions = ({
  onAccept,
  onReject,
  showActions
}) => {
  if (!showActions) return null;
  return /* @__PURE__ */ jsxs("div", { className: "flex gap-2", children: [
    /* @__PURE__ */ jsx(
      Button,
      {
        variant: "ghost",
        className: "px-1 opacity-50 hover:opacity-100",
        icon: "cross-small",
        size: "sm",
        onClick: onReject,
        children: "Reject All"
      }
    ),
    /* @__PURE__ */ jsx(
      Button,
      {
        variant: "ghost",
        icon: "checkmark-2-small",
        className: "px-1",
        size: "sm",
        onClick: onAccept,
        children: "Accept All"
      }
    )
  ] });
};
const ChatStatus = ({
  status = "online",
  isLoading = false,
  isInterrupted = false,
  text: customText,
  icon: customIcon,
  actions,
  messages = [],
  workbooks
}) => {
  var _a, _b;
  const currentStatus = messages == null ? void 0 : messages.find(
    //@ts-ignore
    (m) => {
      var _a2;
      return m.role === "assistant" && !!((_a2 = m.data) == null ? void 0 : _a2["inProgress"]);
    }
  );
  const runningTool = messages == null ? void 0 : messages.find((m) => {
    var _a2;
    const toolInvocations = (_a2 = m.parts) == null ? void 0 : _a2.filter((p) => p.type === "tool-invocation");
    if (!(toolInvocations == null ? void 0 : toolInvocations.length)) return false;
    return toolInvocations.some(
      (tool) => isToolRunning(tool.toolInvocation.state) && !isToolCancelled(tool.toolInvocation)
    );
  });
  const runningToolInvocation = (_b = (_a = runningTool == null ? void 0 : runningTool.parts) == null ? void 0 : _a.find(
    (part) => part.type === "tool-invocation" && "toolInvocation" in part && isToolRunning(part.toolInvocation.state) && !isToolCancelled(part.toolInvocation)
  )) == null ? void 0 : _b.toolInvocation;
  const getStatusText2 = () => {
    if (runningToolInvocation && !isInterrupted)
      return getToolMessage(runningToolInvocation, "running", workbooks);
    if (customText) return customText;
    if (isLoading) return "Agent is thinking...";
    if (currentStatus == null ? void 0 : currentStatus.content) {
      return typeof currentStatus.content === "string" ? currentStatus.content : JSON.stringify(currentStatus.content);
    }
    switch (status) {
      case "error":
        return "Agent encountered an error";
      case "initializing":
        return "Initializing...";
      case "online":
        return isInterrupted ? "Paused: Awaiting user response" : "";
      default:
        return "Disconnected";
    }
  };
  const hasInProgressMessage = messages == null ? void 0 : messages.some((m) => {
    if (m.role === "assistant" && Array.isArray(m.parts)) {
      return m.parts.some(
        (part) => part.type === "tool-invocation" && part.toolInvocation && isToolRunning(part.toolInvocation.state) && !isToolCancelled(part.toolInvocation)
      );
    }
    return false;
  });
  const statusActions = useMemo(() => {
    if (!(actions == null ? void 0 : actions.showActions) || isLoading) return null;
    return /* @__PURE__ */ jsx(
      BulkDecisionActions,
      {
        onAccept: actions.onAccept,
        onReject: actions.onReject,
        showActions: actions.showActions
      }
    );
  }, [actions, isLoading]);
  const isDefaultOnlineState = status === "online" && !isLoading && !isInterrupted && !runningToolInvocation && !customText && !statusActions && !hasInProgressMessage;
  const isVisible = !isDefaultOnlineState;
  const getStatusType = () => {
    if (hasInProgressMessage || isLoading) return "loading";
    if (isInterrupted) return "paused";
    if (status === "error") return "error";
    if (status === "online") return "online";
    return "custom";
  };
  const customIconElement = customIcon ? /* @__PURE__ */ jsx(Icon, { name: customIcon, size: 8 }) : void 0;
  return /* @__PURE__ */ jsx(
    ChatStatusPrimitive,
    {
      statusType: getStatusType(),
      text: getStatusText2(),
      icon: customIconElement,
      actions: statusActions,
      isVisible,
      isLoading
    }
  );
};
const getFormattedTime = (timestamp) => {
  const now = /* @__PURE__ */ new Date();
  const operationTime = new Date(timestamp);
  const diffInMilliseconds = now.getTime() - operationTime.getTime();
  const diffInMinutes = Math.floor(diffInMilliseconds / (1e3 * 60));
  const diffInHours = Math.floor(diffInMilliseconds / (1e3 * 60 * 60));
  const diffInDays = Math.floor(diffInMilliseconds / (1e3 * 60 * 60 * 24));
  const diffInWeeks = Math.floor(diffInMilliseconds / (1e3 * 60 * 60 * 24 * 7));
  if (diffInMinutes < 1) {
    return "just now";
  } else if (diffInMinutes < 60) {
    return `${diffInMinutes} ${diffInMinutes === 1 ? "min" : "mins"} ago`;
  } else if (diffInHours < 24) {
    return `${diffInHours} ${diffInHours === 1 ? "hour" : "hours"} ago`;
  } else if (diffInDays < 7) {
    return `${diffInDays} ${diffInDays === 1 ? "day" : "days"} ago`;
  } else if (diffInWeeks < 4) {
    return `${diffInWeeks} ${diffInWeeks === 1 ? "week" : "weeks"} ago`;
  } else {
    const options = {
      year: "numeric",
      month: "short",
      day: "numeric"
    };
    return operationTime.toLocaleDateString(void 0, options);
  }
};
const getOperationIcon = (toolName) => {
  if (toolName.toLowerCase().includes("map") || toolName.toLowerCase().includes("merge")) {
    return /* @__PURE__ */ jsx(Icon, { name: "merge", className: "h-4 w-4 text-gray-500" });
  } else if (toolName.toLowerCase().includes("split") || toolName.toLowerCase().includes("extract")) {
    return /* @__PURE__ */ jsx(Icon, { name: "split", className: "h-4 w-4 text-gray-500" });
  } else if (toolName.toLowerCase().includes("transform")) {
    return /* @__PURE__ */ jsx(
      Icon,
      {
        name: "arrow-rotate-clockwise-right",
        className: "h-4 w-4 text-gray-500"
      }
    );
  } else if (toolName.toLowerCase().includes("format")) {
    return /* @__PURE__ */ jsx(Icon, { name: "text-1", className: "h-4 w-4 text-gray-500" });
  } else if (toolName.toLowerCase().includes("translate")) {
    return /* @__PURE__ */ jsx(Icon, { name: "text-1", className: "h-4 w-4 text-gray-500" });
  } else if (toolName.toLowerCase().includes("delete")) {
    return /* @__PURE__ */ jsx(Icon, { name: "trash-can-2", className: "h-4 w-4 text-gray-500" });
  } else if (toolName.toLowerCase().includes("dedup")) {
    return /* @__PURE__ */ jsx(Icon, { name: "files", className: "h-4 w-4 text-gray-500" });
  } else if (toolName.toLowerCase().includes("undo") || toolName.toLowerCase().includes("revert")) {
    return /* @__PURE__ */ jsx(Icon, { name: "history", className: "h-4 w-4 text-gray-500" });
  } else {
    return /* @__PURE__ */ jsx(Icon, { name: "toolbox", className: "h-4 w-4 text-gray-500" });
  }
};
const getOperationDetails = (toolName) => {
  let title = "Operation";
  if (toolName) {
    const toolNameLower = toolName.toLowerCase();
    if (toolNameLower.includes("map") || toolNameLower.includes("merge")) {
      title = "Merge";
    } else if (toolNameLower.includes("split") || toolNameLower.includes("extract")) {
      title = "Split";
    } else if (toolNameLower.includes("translate")) {
      title = "Translate";
    } else if (toolNameLower.includes("delete") || toolNameLower.includes("remove")) {
      title = "Delete";
    } else if (toolNameLower.includes("format") || toolNameLower.includes("reformat")) {
      title = "Reformat";
    } else if (toolNameLower.includes("dedup") || toolNameLower.includes("deduplicate")) {
      title = "Dedup";
    } else if (toolNameLower.includes("undo") || toolNameLower.includes("revert")) {
      title = "Undo";
    } else if (toolNameLower.includes("normalize")) {
      title = "Normalize";
    } else if (toolNameLower.includes("transform")) {
      title = "Transform";
    } else {
      title = toolName.split(/[_-]/).map(
        (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      ).join(" ");
    }
  }
  return { title };
};
const OperationItem = ({
  operation,
  isRunning = false,
  onLinkClick,
  onStepBackClick,
  isReverted = false
}) => {
  const { toolName, timestamp, targetOperationId, sheetId } = operation;
  const [isHovering, setIsHovering] = useState(false);
  const { title } = getOperationDetails(toolName);
  const handleLinkClick = () => {
    if (onLinkClick) {
      onLinkClick(operation);
    }
  };
  const handleStepBackClick = () => {
    if (onStepBackClick) {
      onStepBackClick(operation);
    }
  };
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "flex items-center justify-between px-2 py-1 rounded-md mb-1",
        !isReverted && title !== "Undo" && "text-gray-400 hover:bg-gray-100",
        isReverted && "line-through opacity-50 text-gray-400"
      ),
      onMouseEnter: () => setIsHovering(isReverted ? false : true),
      onMouseLeave: () => setIsHovering(false),
      children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-center", children: [
          /* @__PURE__ */ jsx("div", { className: "flex-shrink-0 mr-3", children: getOperationIcon(toolName) }),
          /* @__PURE__ */ jsx("div", { children: /* @__PURE__ */ jsxs("div", { className: "flex items-center", children: [
            /* @__PURE__ */ jsx("span", { className: "text-sm font-medium text-gray-900", children: title }),
            sheetId && /* @__PURE__ */ jsxs(
              "span",
              {
                className: cn(
                  "ml-1.5 mr-1 inline-flex items-center px-3 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100 transition-opacity duration-200",
                  isHovering ? "opacity-100" : "opacity-0"
                ),
                children: [
                  /* @__PURE__ */ jsx(Icon, { name: "table", className: "h-3 w-3 mr-0.5 text-blue-500" }),
                  sheetId
                ]
              }
            ),
            targetOperationId && /* @__PURE__ */ jsx("span", { className: "text-sm font-medium opacity-40 px-1", children: targetOperationId }),
            isRunning && /* @__PURE__ */ jsx("span", { className: "ml-2", children: /* @__PURE__ */ jsx(
              Icon,
              {
                name: "loader",
                className: "h-4 w-4 text-blue-500 animate-spin pointer-events-none"
              }
            ) })
          ] }) })
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "flex items-center relative", children: [
          /* @__PURE__ */ jsx(
            "div",
            {
              className: cn(
                "transition-opacity duration-200 pointer-events-none absolute right-0 w-[150px] text-right",
                isHovering && title !== "Undo" ? "opacity-0" : "opacity-100"
              ),
              children: /* @__PURE__ */ jsx("div", { className: "text-xs text-gray-400", children: getFormattedTime(timestamp) })
            }
          ),
          title !== "Undo" && /* @__PURE__ */ jsx(
            "div",
            {
              className: cn(
                "transition-opacity duration-200",
                isHovering ? "opacity-100" : "opacity-0"
              ),
              children: /* @__PURE__ */ jsxs("div", { className: "flex space-x-2", children: [
                onLinkClick && /* @__PURE__ */ jsx(
                  "button",
                  {
                    className: "p-1 rounded hover:bg-gray-200",
                    onClick: handleLinkClick,
                    children: /* @__PURE__ */ jsx(
                      Icon,
                      {
                        name: "pencil",
                        className: "h-4 w-4 text-gray-500 pointer-events-none"
                      }
                    )
                  }
                ),
                onStepBackClick && /* @__PURE__ */ jsx(
                  "button",
                  {
                    className: "p-1 rounded hover:bg-gray-200",
                    onClick: handleStepBackClick,
                    children: /* @__PURE__ */ jsx(
                      Icon,
                      {
                        name: "step-back",
                        className: "h-4 w-4 text-gray-500 pointer-events-none"
                      }
                    )
                  }
                )
              ] })
            }
          )
        ] })
      ]
    }
  );
};
const OperationList = ({
  operationItems = [],
  className,
  onLinkClick,
  onStepBackClick
}) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [_displayedOperations, setDisplayedOperations] = useState(
    []
  );
  const relevantOperations = operationItems.filter(
    (op) => !op.toolName.toLowerCase().includes("suggestnextstepstool")
  );
  const revertOperations = operationItems.filter((op) => op.toolName === "revert").map((op) => op.targetOperationId);
  useEffect(() => {
    const filteredOperations = relevantOperations.filter(
      (op) => op.status === "completed"
    );
    const sortedOperations = [...filteredOperations].sort(
      (a, b) => b.timestamp - a.timestamp
    );
    setDisplayedOperations(sortedOperations);
  }, [operationItems]);
  const pastOperations = useMemo(() => {
    return relevantOperations.filter((op) => op.status === "completed" || op.status === "pending").sort((a, b) => b.timestamp - a.timestamp).map((op) => ({
      ...op,
      isReverted: revertOperations.includes(op.id)
    }));
  }, [relevantOperations]);
  const completedCorrelationIds = useMemo(() => {
    const ids = /* @__PURE__ */ new Set();
    pastOperations.forEach((op) => {
      if (op.correlationId) {
        ids.add(op.correlationId);
      }
    });
    return ids;
  }, [pastOperations]);
  const runningOperations = useMemo(() => {
    return relevantOperations.filter((op) => {
      var _a;
      if (!((_a = op.status) == null ? void 0 : _a.includes("starting"))) {
        return false;
      }
      if (op.correlationId && completedCorrelationIds.has(op.correlationId)) {
        return false;
      }
      return true;
    }).sort((a, b) => b.timestamp - a.timestamp);
  }, [relevantOperations, completedCorrelationIds]);
  const runningOperation = runningOperations.length > 0 ? runningOperations[0] : null;
  const totalCount = runningOperations.length + pastOperations.length;
  if (relevantOperations.length === 0) {
    return null;
  }
  return /* @__PURE__ */ jsxs("div", { className: cn("overflow-hidden pb-2.5", className), children: [
    /* @__PURE__ */ jsxs(
      "button",
      {
        onClick: () => setIsCollapsed(!isCollapsed),
        className: "px-2 py-1.5 w-full flex items-center justify-between hover:bg-gray-100 cursor-pointer rounded-sm",
        children: [
          /* @__PURE__ */ jsxs("div", { className: "flex items-center", children: [
            /* @__PURE__ */ jsx(Icon, { name: "history", className: "h-4 w-4 mr-2 text-gray-500" }),
            /* @__PURE__ */ jsxs("h3", { className: "text-sm font-medium text-gray-700", children: [
              totalCount,
              " Operations"
            ] })
          ] }),
          /* @__PURE__ */ jsx(
            Icon,
            {
              name: "chevron-bottom",
              color: "gray",
              size: 12,
              className: cn(
                "h-4 w-4 transition-transform duration-200",
                !isCollapsed && "transform rotate-180"
              )
            }
          )
        ]
      }
    ),
    !isCollapsed && /* @__PURE__ */ jsxs("div", { className: "max-h-96 overflow-y-auto mt-1", children: [
      runningOperation && /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx("div", { className: "px-2 py-1 text-xs font-medium text-gray-500", children: "Running" }),
        /* @__PURE__ */ jsx(
          OperationItem,
          {
            operation: runningOperation,
            isRunning: true
          },
          runningOperation.id
        )
      ] }),
      pastOperations.length > 0 && /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx("div", { className: "px-2 py-1 text-xs font-medium text-gray-500", children: "Past" }),
        pastOperations.map((operation) => /* @__PURE__ */ jsx(
          OperationItem,
          {
            operation,
            isRunning: false,
            isReverted: operation.isReverted,
            onLinkClick,
            onStepBackClick
          },
          operation.id
        ))
      ] })
    ] })
  ] });
};
const SuggestionCard = ({
  title,
  description,
  iconName,
  onClick,
  className,
  collapsed = false
}) => {
  if (collapsed) {
    return /* @__PURE__ */ jsx(
      "div",
      {
        onClick,
        className: cn(
          "flex items-center rounded-md border border-table-border px-3 py-1.5 bg-white hover:bg-gray-100 cursor-pointer transition-colors duration-200 theme-static",
          className
        ),
        children: /* @__PURE__ */ jsx("span", { className: "text-xs font-medium whitespace-nowrap", children: title })
      }
    );
  }
  return /* @__PURE__ */ jsxs(
    "div",
    {
      onClick,
      className: cn(
        "flex flex-col rounded-md border border-table-border p-3 min-w-[200px] max-w-[260px] bg-white hover:bg-gray-100 cursor-pointer transition-colors duration-200 theme-static",
        className
      ),
      children: [
        /* @__PURE__ */ jsx(Icon, { name: iconName, size: 16, className: "text-foreground-50 mb-1.5" }),
        /* @__PURE__ */ jsx("h3", { className: "font-medium text-sm mt-1 mb-1.5", children: title }),
        /* @__PURE__ */ jsx("p", { className: "text-xs text-foreground-80", children: description })
      ]
    }
  );
};
const SuggestionCardList = ({
  suggestions,
  onAcceptAllSuggestions,
  isLoadingSuggestions,
  className,
  hasMessages = false
}) => {
  const scrollContainerRef = useRef(null);
  const [showLeftScroll, setShowLeftScroll] = useState(false);
  const [showRightScroll, setShowRightScroll] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const checkScrollButtons = () => {
    if (!scrollContainerRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
    setShowLeftScroll(scrollLeft > 0);
    setShowRightScroll(scrollLeft < scrollWidth - clientWidth - 5);
  };
  useEffect(() => {
    checkScrollButtons();
    const resizeObserver = new ResizeObserver(() => {
      checkScrollButtons();
    });
    if (scrollContainerRef.current) {
      resizeObserver.observe(scrollContainerRef.current);
    }
    return () => {
      resizeObserver.disconnect();
    };
  }, [suggestions]);
  if (suggestions === null || isDismissed) return null;
  if (isLoadingSuggestions) {
    return /* @__PURE__ */ jsx("div", { className: cn("relative w-full hide-scrollbar", className), children: /* @__PURE__ */ jsxs("div", { className: "absolute font-light flex items-center text-[10px] left-[10px] opacity-50 top-[-16px] z-10 flex items-center justify-between", children: [
      /* @__PURE__ */ jsx(Icon, { name: "loader", className: "animate-spin mr-1", size: 12 }),
      /* @__PURE__ */ jsx("span", { children: "Generating Suggestions..." })
    ] }) });
  }
  if (!suggestions.length) return null;
  return /* @__PURE__ */ jsxs("div", { className: cn("relative w-full hide-scrollbar", className), children: [
    /* @__PURE__ */ jsxs("div", { className: "absolute right-[-6px] top-[-26px] z-10 flex items-center justify-between", children: [
      onAcceptAllSuggestions && /* @__PURE__ */ jsx(
        Button,
        {
          variant: "ghost",
          size: "sm",
          className: "h-6 flex items-center justify-center font-light mt-0.5 text-[10px] uppercase p-0 opacity-50 hover:opacity-80 hover:bg-base-white transition-opacity duration-200 theme-static",
          onClick: () => onAcceptAllSuggestions == null ? void 0 : onAcceptAllSuggestions(),
          children: "Accept All"
        }
      ),
      /* @__PURE__ */ jsx(
        Button,
        {
          variant: "ghost",
          size: "sm",
          className: "h-6 w-6 min-h-0 rounded-full flex items-center justify-center p-0 opacity-50 hover:opacity-80 hover:bg-base-white transition-opacity duration-200 theme-static",
          onClick: () => setIsDismissed(true),
          children: /* @__PURE__ */ jsx(Icon, { name: "cross-large", size: 12 })
        }
      )
    ] }),
    /* @__PURE__ */ jsx(
      "div",
      {
        className: "absolute left-0 top-0 h-full w-8 z-[1] bg-gradient-to-r from-base-white via-base-white/50 to-transparent pointer-events-none transition-opacity duration-300 theme-static",
        style: { opacity: showLeftScroll ? 1 : 0 }
      }
    ),
    /* @__PURE__ */ jsx(
      "div",
      {
        className: "absolute right-0 top-0 h-full w-8 z-[1] bg-gradient-to-l from-base-white via-base-white/50 to-transparent pointer-events-none transition-opacity duration-300 theme-static",
        style: { opacity: showRightScroll ? 1 : 0 }
      }
    ),
    /* @__PURE__ */ jsx(
      "div",
      {
        ref: scrollContainerRef,
        className: cn(
          "flex overflow-x-auto gap-2.5 py-1 px-1 hide-scrollbar",
          hasMessages ? "gap-2" : "gap-2.5"
        ),
        onScroll: checkScrollButtons,
        children: suggestions.map((suggestion, index) => /* @__PURE__ */ jsx(
          SuggestionCard,
          {
            ...suggestion,
            collapsed: hasMessages
          },
          `suggestion-${index}`
        ))
      }
    )
  ] });
};
const getConfigs = (mentions) => {
  return [createMentionConfig(mentions)];
};
const useAddMentions = ({
  mentions,
  mentionToAdd,
  onMentionAdded,
  mentionsContextRef
}) => {
  const prevMentionsRef = useRef(mentions);
  const configs = useMemo(() => {
    return mentions.length > 0 ? getConfigs(mentions) : [];
  }, [mentions]);
  const isMentionAlreadyPresent = useCallback(
    (mentionItem) => {
      var _a;
      if (!((_a = mentionsContextRef.current) == null ? void 0 : _a.inputElement)) {
        return false;
      }
      const inputElement = mentionsContextRef.current.inputElement;
      const inputContent = inputElement.textContent || inputElement.innerText || "";
      if (inputContent.includes(mentionItem.displayText)) {
        return true;
      }
      return false;
    },
    []
  );
  const addMentionProgrammatically = useCallback(
    (mentionItem) => {
      var _a;
      if (!((_a = mentionsContextRef.current) == null ? void 0 : _a.inputElement)) {
        return false;
      }
      if (!mentionsContextRef.current.insertFragment) {
        console.warn("Cannot add mention: insertFragment method not available");
        return false;
      }
      if (isMentionAlreadyPresent(mentionItem)) {
        return true;
      }
      try {
        const inputElement = mentionsContextRef.current.inputElement;
        inputElement.focus();
        mentionsContextRef.current.insertFragment(mentionItem.ref);
        try {
          const inputEvent = new InputEvent("input", {
            inputType: "insertText",
            data: " ",
            bubbles: true,
            cancelable: true
          });
          inputElement.dispatchEvent(inputEvent);
        } catch (innerError) {
          console.error(
            "Error inserting space to end of added mention:",
            innerError
          );
        }
        return true;
      } catch (error) {
        console.error("Error adding mention programmatically:", error);
        return false;
      }
    },
    [isMentionAlreadyPresent]
  );
  const waitForMentionsContext = useCallback(() => {
    return new Promise((resolve) => {
      var _a, _b;
      if (((_a = mentionsContextRef.current) == null ? void 0 : _a.inputElement) && ((_b = mentionsContextRef.current) == null ? void 0 : _b.insertFragment)) {
        resolve(true);
        return;
      }
      const intervalId = setInterval(() => {
        var _a2, _b2;
        if (((_a2 = mentionsContextRef.current) == null ? void 0 : _a2.inputElement) && ((_b2 = mentionsContextRef.current) == null ? void 0 : _b2.insertFragment)) {
          clearInterval(intervalId);
          clearTimeout(timeoutId);
          resolve(true);
        }
      }, 200);
      const timeoutId = setTimeout(() => {
        clearInterval(intervalId);
        resolve(false);
      }, 2e3);
    });
  }, []);
  useEffect(() => {
    var _a;
    const mentionsChanged = JSON.stringify(prevMentionsRef.current) !== JSON.stringify(mentions);
    if (mentionsChanged && ((_a = mentionsContextRef.current) == null ? void 0 : _a.configs)) {
      mentionsContextRef.current.configs = configs;
      prevMentionsRef.current = mentions;
    }
  }, [mentions, configs]);
  useEffect(() => {
    if (!mentionToAdd) {
      return;
    }
    let isCancelled = false;
    const addMentionAsync = async () => {
      try {
        await waitForMentionsContext();
        if (isCancelled) {
          return;
        }
        if (isMentionAlreadyPresent(mentionToAdd)) {
          onMentionAdded == null ? void 0 : onMentionAdded();
          return;
        }
        const success = addMentionProgrammatically(mentionToAdd);
        if (success) {
          onMentionAdded == null ? void 0 : onMentionAdded();
        } else {
          console.warn("Failed to add mention programmatically");
        }
      } catch (error) {
        console.error("Error in addMentionAsync:", error);
      }
    };
    addMentionAsync();
    return () => {
      isCancelled = true;
    };
  }, [
    mentionToAdd,
    addMentionProgrammatically,
    onMentionAdded,
    waitForMentionsContext,
    isMentionAlreadyPresent
  ]);
  useEffect(() => {
    if (mentionsContextRef.current) {
      mentionsContextRef.current.addMention = addMentionProgrammatically;
    }
  }, [addMentionProgrammatically]);
  return {
    configs,
    addMentionProgrammatically,
    isMentionAlreadyPresent,
    waitForMentionsContext
  };
};
const ChatUI = ({
  messages,
  header = {},
  input = {},
  status = {},
  emptyState = {},
  className,
  workbooks,
  tools,
  onRevert,
  onOptionSelect,
  onMentionClick,
  onStop,
  acceptedFileTypes,
  maxFileSize,
  mentions = [],
  suggestions = [],
  onAcceptAllSuggestions,
  isLoadingSuggestions = false,
  operationItems = [],
  onLinkClick,
  onStepBackClick,
  reroll,
  isLoadingChat = false,
  mentionToAdd,
  onMentionAdded,
  statusMessagePreviewLength,
  renderMessage = (message) => /* @__PURE__ */ jsx(
    ChatMessage,
    {
      ...message,
      onOptionSelect,
      onMentionClick,
      reroll,
      tools,
      statusMessagePreviewLength
    }
  ),
  renderHeader = (props) => /* @__PURE__ */ jsx(ChatHeader, { ...props }),
  renderInput = (props) => /* @__PURE__ */ jsx(ChatInput, { ...props }),
  renderStatus = (props) => /* @__PURE__ */ jsx(ChatStatus, { ...props }),
  renderEmptyState = (props) => /* @__PURE__ */ jsx(ChatEmptyState, { ...props }),
  contentContainerClassName
}) => {
  const messagesEndRef = React.useRef(null);
  const messagesContainerRef = React.useRef(null);
  const mentionsContextRef = useRef(null);
  const { configs } = useAddMentions({
    mentions,
    mentionToAdd,
    onMentionAdded,
    mentionsContextRef
  });
  const mentionsKey = `mentions-provider-instance`;
  const [globalRollbacks, setGlobalRollbacks] = useState([]);
  const [archiveToggles, setArchiveToggles] = useState(
    {}
  );
  const [justSubmitted, setJustSubmitted] = useState(false);
  const [needsPostSubmissionScroll, setNeedsPostSubmissionScroll] = useState(false);
  const safeOperationItems = operationItems || [];
  const showOperationList = safeOperationItems.length > 0;
  const showHeaderBar = !!header || showOperationList;
  const defaultStatusProps = {
    status: "online",
    isLoading: false,
    isInterrupted: false,
    text: "",
    messages,
    ...status
  };
  useEffect(() => {
    if (messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      container.scrollTop = container.scrollHeight;
    }
    if (justSubmitted) {
      setNeedsPostSubmissionScroll(true);
    }
  }, [messages, justSubmitted]);
  useEffect(() => {
    if (needsPostSubmissionScroll) {
      requestAnimationFrame(() => {
        if (messagesContainerRef.current) {
          const container = messagesContainerRef.current;
          container.scrollTop = container.scrollHeight;
        }
      });
      setNeedsPostSubmissionScroll(false);
    }
  }, [
    defaultStatusProps.isLoading,
    defaultStatusProps.text,
    needsPostSubmissionScroll
  ]);
  useEffect(() => {
    var _a, _b;
    if (justSubmitted) {
      let currentHeight = null;
      if (((_a = mentionsContextRef.current) == null ? void 0 : _a.inputElement) && mentionsContextRef.current.inputElement.style) {
        currentHeight = mentionsContextRef.current.inputElement.style.height;
      }
      if (mentionsContextRef.current && typeof mentionsContextRef.current.setValue === "function") {
        mentionsContextRef.current.setValue("");
      }
      if (currentHeight && ((_b = mentionsContextRef.current) == null ? void 0 : _b.inputElement)) {
        mentionsContextRef.current.inputElement.style.height = currentHeight;
      }
      setJustSubmitted(false);
    }
  }, [justSubmitted]);
  const handleSubmitWithMentions = (e, options) => {
    var _a;
    (_a = input.handleSubmit) == null ? void 0 : _a.call(input, e, options);
    setJustSubmitted(true);
    requestAnimationFrame(() => {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      }
    });
    if (mentionsContextRef.current && typeof mentionsContextRef.current.setValue === "function") {
      mentionsContextRef.current.setValue("");
    }
    if (input.handleInputChange) {
      input.handleInputChange({ target: { value: "" } });
    }
  };
  const hasMentions = mentions.length > 0;
  const defaultInputProps = {
    // Ensure input is always a string
    input: typeof input.input === "string" ? input.input : "",
    // Our wrapper for handleSubmit to ensure input clearing
    handleSubmit: handleSubmitWithMentions,
    // Only set these if not provided by parent
    placeholder: input.placeholder || "How can I help you?",
    isLoading: input.isLoading !== void 0 ? input.isLoading : false,
    acceptedFileTypes: input.acceptedFileTypes || acceptedFileTypes,
    maxFileSize: input.maxFileSize || maxFileSize,
    // Ensure handleInputChange exists
    handleInputChange: input.handleInputChange || (() => {
    }),
    // Add isGenerating and onStop properties from input or use default
    isGenerating: input.isGenerating !== void 0 ? input.isGenerating : false,
    onStop: input.onStop || onStop,
    model: input.model || null,
    onModelChange: input.onModelChange || (() => {
    }),
    // Pass through any other props
    ...input.disabled !== void 0 ? { disabled: input.disabled } : {},
    autoFocus: (input == null ? void 0 : input.autoFocus) || false,
    hasMentions
  };
  const conversationHandleRevert = ({
    snapshotId,
    snapshotTime,
    messageIndex: newIndex,
    partOrder: newPartOrder,
    toolCallId
  }) => {
    if (snapshotId && snapshotTime) {
      onRevert == null ? void 0 : onRevert(snapshotId, snapshotTime, toolCallId, newIndex);
    }
    const newBoundary = {
      rollbackMessageIndex: newIndex,
      partOrder: newPartOrder,
      timestamp: Date.now(),
      cutoff: messages.length
    };
    setGlobalRollbacks((prev) => {
      if (prev.length === 0) return [newBoundary];
      if (newBoundary.rollbackMessageIndex < prev[0].rollbackMessageIndex) {
        return [newBoundary];
      }
      const last = prev[prev.length - 1];
      if (newBoundary.rollbackMessageIndex < last.cutoff) {
        const updatedPartOrder = Math.min(last.partOrder, newBoundary.partOrder);
        const merged = {
          rollbackMessageIndex: Math.min(
            last.rollbackMessageIndex,
            newBoundary.rollbackMessageIndex
          ),
          partOrder: updatedPartOrder,
          timestamp: newBoundary.timestamp,
          // use new rollback timestamp
          cutoff: Math.max(last.cutoff, newBoundary.cutoff)
        };
        return [...prev.slice(0, prev.length - 1), merged];
      } else {
        return [...prev, newBoundary];
      }
    });
    setArchiveToggles((prev) => ({ ...prev, [newIndex]: false }));
  };
  const isLastMessageInConvo = (messageIndex) => messageIndex === messages.length - 1;
  let groups = [];
  if (globalRollbacks.length === 0) {
    groups.push({ type: "normal", start: 0, end: messages.length });
  } else {
    const sortedBoundaries = [...globalRollbacks].sort(
      (a, b) => a.rollbackMessageIndex - b.rollbackMessageIndex
    );
    let currentIndex = 0;
    sortedBoundaries.forEach((boundary) => {
      if (currentIndex < boundary.rollbackMessageIndex) {
        groups.push({
          type: "normal",
          start: currentIndex,
          end: boundary.rollbackMessageIndex
        });
      }
      groups.push({
        type: "rolled",
        start: boundary.rollbackMessageIndex,
        end: boundary.cutoff,
        boundary
      });
      currentIndex = Math.max(currentIndex, boundary.cutoff);
    });
    if (currentIndex < messages.length) {
      groups.push({
        type: "normal",
        start: currentIndex,
        end: messages.length
      });
    }
  }
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "flex flex-col h-full w-full overflow-hidden bg-base-white theme-static relative",
        className
      ),
      children: [
        showHeaderBar && /* @__PURE__ */ jsxs(
          "div",
          {
            className: cn({
              "h-12": !showOperationList
            }),
            children: [
              !!header && renderHeader({
                title: (header == null ? void 0 : header.title) || "Chat",
                allowCollapse: header == null ? void 0 : header.allowCollapse,
                onCollapse: header == null ? void 0 : header.onCollapse,
                isCollapsed: header == null ? void 0 : header.isCollapsed,
                allowClose: header == null ? void 0 : header.allowClose,
                onClose: header == null ? void 0 : header.onClose,
                className: header == null ? void 0 : header.className
              }),
              showOperationList && /* @__PURE__ */ jsx("div", { className: "px-3 w-full", children: /* @__PURE__ */ jsx(
                OperationList,
                {
                  operationItems: safeOperationItems,
                  onLinkClick,
                  onStepBackClick
                }
              ) })
            ]
          }
        ),
        /* @__PURE__ */ jsx(
          "div",
          {
            className: cn(
              "flex-1 w-full overflow-y-auto overflow-x-hidden p-4 space-y-3",
              "min-h-0",
              contentContainerClassName
            ),
            ref: messagesContainerRef,
            id: "chat-ui-content-container",
            children: messages.length === 0 ? renderEmptyState({
              ...emptyState,
              isLoadingChat
            }) : /* @__PURE__ */ jsxs("div", { className: "h-full w-full flex flex-col", children: [
              /* @__PURE__ */ jsx("div", { className: "flex-1" }),
              /* @__PURE__ */ jsxs("div", { className: "flex flex-col py-0 w-full [&>*:not(:last-child)]:mb-5", children: [
                groups.map((group, groupIdx) => {
                  if (group.type === "normal") {
                    return /* @__PURE__ */ jsx(React.Fragment, { children: messages.slice(group.start, group.end).map((message, idx) => {
                      const messageIndex = group.start + idx;
                      return /* @__PURE__ */ jsx(
                        "div",
                        {
                          className: cn("flex justify-start"),
                          children: renderMessage({
                            ...message,
                            messageIndex,
                            workbooks,
                            onRevert: conversationHandleRevert,
                            onMentionClick,
                            isLastMessage: isLastMessageInConvo(messageIndex),
                            tools
                          })
                        },
                        message.id || idx
                      );
                    }) }, `group-${groupIdx}`);
                  } else {
                    const boundary = group.boundary;
                    return /* @__PURE__ */ jsxs(React.Fragment, { children: [
                      /* @__PURE__ */ jsx("div", { className: cn("flex justify-start"), children: renderMessage({
                        ...messages[boundary.rollbackMessageIndex],
                        messageIndex: boundary.rollbackMessageIndex,
                        workbooks,
                        onRevert: conversationHandleRevert,
                        displayBoundary: boundary.partOrder,
                        onMentionClick,
                        isLastMessage: isLastMessageInConvo(
                          boundary.rollbackMessageIndex
                        ),
                        tools
                      }) }),
                      /* @__PURE__ */ jsxs(
                        Button,
                        {
                          variant: "ghost",
                          size: "sm",
                          className: "mb-2 h-6 min-h-[24px] p-0 !text-foreground-80",
                          onClick: () => setArchiveToggles((prev) => ({
                            ...prev,
                            [boundary.rollbackMessageIndex]: !prev[boundary.rollbackMessageIndex]
                          })),
                          children: [
                            /* @__PURE__ */ jsx("span", { className: "text-md", children: "Archived conversation after rolling back" }),
                            /* @__PURE__ */ jsx(
                              Icon,
                              {
                                name: archiveToggles[boundary.rollbackMessageIndex] ? "chevron-bottom" : "chevron-right",
                                size: 12
                              }
                            )
                          ]
                        }
                      ),
                      archiveToggles[boundary.rollbackMessageIndex] && /* @__PURE__ */ jsx("div", { className: "bg-background-80 p-2", children: /* @__PURE__ */ jsxs("div", { className: "opacity-70 flex flex-col gap-4", children: [
                        /* @__PURE__ */ jsx("div", { className: cn("flex justify-start"), children: renderMessage({
                          ...messages[boundary.rollbackMessageIndex],
                          messageIndex: boundary.rollbackMessageIndex,
                          workbooks,
                          onRevert: conversationHandleRevert,
                          startOrder: boundary.partOrder,
                          isRolledBack: true,
                          onMentionClick,
                          isLastMessage: isLastMessageInConvo(
                            boundary.rollbackMessageIndex
                          ),
                          tools
                        }) }),
                        messages.slice(
                          boundary.rollbackMessageIndex + 1,
                          group.end
                        ).map((message, idx) => {
                          const messageIndex = boundary.rollbackMessageIndex + 1 + idx;
                          return /* @__PURE__ */ jsx(
                            "div",
                            {
                              className: cn("flex justify-start"),
                              children: renderMessage({
                                ...message,
                                messageIndex,
                                workbooks,
                                onRevert: conversationHandleRevert,
                                isRolledBack: true,
                                onMentionClick,
                                isLastMessage: isLastMessageInConvo(messageIndex),
                                tools
                              })
                            },
                            message.id || idx
                          );
                        })
                      ] }) })
                    ] }, `group-${groupIdx}`);
                  }
                }),
                /* @__PURE__ */ jsx("div", { ref: messagesEndRef })
              ] })
            ] })
          }
        ),
        /* @__PURE__ */ jsxs("div", { className: "flex-none w-full bg-white p-4 pt-0 space-y-2", children: [
          suggestions !== null && /* @__PURE__ */ jsx(
            SuggestionCardList,
            {
              suggestions,
              className: "mb-2",
              hasMessages: messages.length > 0,
              isLoadingSuggestions,
              onAcceptAllSuggestions
            }
          ),
          /* @__PURE__ */ jsx(
            "div",
            {
              className: cn(
                "border border-table-border rounded-sm overflow-hidden w-full theme-static",
                !defaultInputProps.isGenerating && "focus-within:border-primary",
                defaultInputProps.isGenerating && "border-primary"
              ),
              children: hasMentions ? /* @__PURE__ */ jsxs(
                RichMentionsProvider,
                {
                  configs,
                  getContext: mentionsContextRef,
                  children: [
                    /* @__PURE__ */ jsxs("div", { className: "transition-all duration-300 ease-in-out bg-white", children: [
                      renderStatus(defaultStatusProps),
                      renderInput(defaultInputProps)
                    ] }),
                    /* @__PURE__ */ jsx(MentionsPopover$1, {})
                  ]
                },
                mentionsKey
              ) : /* @__PURE__ */ jsxs("div", { className: "transition-all duration-300 ease-in-out bg-white", children: [
                renderStatus(defaultStatusProps),
                renderInput(defaultInputProps)
              ] })
            }
          )
        ] })
      ]
    }
  );
};
const ActionCard = ({
  subtitle,
  subtitleIcon,
  badge,
  actions,
  isLoading,
  loadingText = "Applying transformation"
}) => {
  const getBadgeStyles = (variant = "default") => {
    switch (variant) {
      case "success":
        return "bg-green-100 text-green-800 border-green-200";
      case "warning":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "info":
        return "bg-blue-100 text-blue-800 border-blue-200";
      default:
        return "bg-background-90 text-primary border-table-border";
    }
  };
  return /* @__PURE__ */ jsxs("div", { className: "w-full bg-white border border-table-border text-gray-900 font-medium text-md p-2 rounded-sm flex flex-col gap-2 relative", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-start flex-col gap-2", children: [
      badge && /* @__PURE__ */ jsxs(
        "div",
        {
          className: `py-0.5 px-1.5 rounded-sm border text-xs font-medium flex shadow-sm items-center gap-0.5 ${getBadgeStyles(badge.variant)}`,
          children: [
            /* @__PURE__ */ jsx(Icon, { name: badge.icon, size: 12 }),
            /* @__PURE__ */ jsx("span", { children: badge.text })
          ]
        }
      ),
      subtitle && /* @__PURE__ */ jsxs("div", { className: "text-gray-600 text-md font-medium flex p-1 items-start gap-2", children: [
        subtitleIcon && /* @__PURE__ */ jsx(Icon, { name: subtitleIcon, size: 16, className: "text-gray-600" }),
        /* @__PURE__ */ jsx("span", { children: subtitle })
      ] })
    ] }),
    isLoading ? /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-center gap-2 text-gray-600 py-2", children: [
      /* @__PURE__ */ jsx("div", { className: "w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" }),
      /* @__PURE__ */ jsx("span", { className: "text-sm", children: loadingText })
    ] }) : /* @__PURE__ */ jsx("div", { className: "flex items-center gap-1 w-full", children: actions.map((action, index) => /* @__PURE__ */ jsx("div", { className: "flex-1 p-1", children: /* @__PURE__ */ jsxs(
      Button,
      {
        variant: action.variant,
        size: "sm",
        onClick: action.onClick,
        disabled: action.disabled || action.loading,
        icon: action.icon,
        className: `
                  w-full text-md font-medium px-2 py-1.5 rounded flex items-center justify-center gap-1 
                  transition-all duration-200 ease-out
                  ${action.variant === "primary" ? "bg-gray-800 hover:bg-gray-900 !text-white !border-0" : action.variant === "outline" ? "border border-table-border bg-white hover:bg-gray-100 text-gray-700 hover:border-gray-300" : "bg-white hover:bg-gray-25 text-gray-700 border border-gray-200 hover:border-gray-300"}
                `,
        children: [
          action.loading && /* @__PURE__ */ jsx("div", { className: "w-3 h-3 border border-current border-t-transparent rounded-full animate-spin mr-1" }),
          /* @__PURE__ */ jsx("span", { children: action.label })
        ]
      }
    ) }, `${action.label}-${index}`)) })
  ] });
};
function MentionsPopover() {
  var _a;
  const {
    opened,
    index,
    results,
    setActiveItemIndex,
    selectItem,
    inputElement
  } = React.useContext(RichMentionsContext);
  const popoverRef = React.useRef(null);
  const [popoverHeight, setPopoverHeight] = React.useState(0);
  const onHoverItem = (index2) => () => setActiveItemIndex(index2);
  React.useLayoutEffect(() => {
    if (popoverRef.current) {
      setPopoverHeight(popoverRef.current.offsetHeight);
    }
  }, [results.length]);
  if (!opened || !results.length) {
    return null;
  }
  const richMentionsInput = document.querySelector(
    '[data-testid="rich-mentions-input"]'
  );
  const actionCardContainer = (richMentionsInput == null ? void 0 : richMentionsInput.closest('[role="dialog"]')) || (richMentionsInput == null ? void 0 : richMentionsInput.closest(".modal")) || ((_a = richMentionsInput == null ? void 0 : richMentionsInput.parentElement) == null ? void 0 : _a.parentElement) || document.querySelector('[role="dialog"]') || document.querySelector(".modal") || document.body;
  if (!actionCardContainer) {
    return null;
  }
  const containerRect = actionCardContainer.getBoundingClientRect();
  const popoverWidth = 240;
  let left = opened.x - containerRect.left + 3;
  let top = opened.y - containerRect.top - 2;
  const viewportHeight = window.innerHeight;
  const popoverBottom = opened.y + popoverHeight + 25;
  if (popoverBottom > viewportHeight) {
    top = opened.y - containerRect.top - popoverHeight - 15;
  }
  if (top < 16) {
    top = 16;
  }
  const maxLeft = containerRect.width - popoverWidth - 16;
  if (left > maxLeft) left = maxLeft;
  if (left < 16) left = 16;
  return createPortal(
    /* @__PURE__ */ jsx(
      "div",
      {
        ref: popoverRef,
        className: "absolute bg-white p-0 border border-gray-200 shadow-lg text-sm flex flex-col w-full max-w-[240px] max-h-[300px] overflow-y-auto rounded-md z-[99999] pointer-events-auto",
        style: { left: `${left}px`, top: `${top}px` },
        "data-testid": "mentions-popover",
        children: results.map((item, i) => {
          const displayText = item.displayText || item.name || "";
          return /* @__PURE__ */ jsxs(
            "button",
            {
              type: "button",
              className: `w-full p-2 cursor-pointer hover:bg-gray-100 rounded-md flex items-center justify-between ${i === index ? "bg-gray-100" : ""}`,
              onClick: () => {
                selectItem(item);
                setTimeout(() => {
                  if (inputElement) {
                    inputElement.focus();
                    const range = document.createRange();
                    const selection = window.getSelection();
                    if (selection) {
                      range.selectNodeContents(inputElement);
                      range.collapse(false);
                      selection.removeAllRanges();
                      selection.addRange(range);
                    }
                  }
                }, 0);
              },
              onMouseOver: onHoverItem(i),
              children: [
                /* @__PURE__ */ jsxs("span", { className: "flex items-center min-w-0 flex-shrink overflow-hidden mr-1.5", children: [
                  /* @__PURE__ */ jsx("span", { className: "truncate max-w-[120px]", children: displayText }),
                  item.type && /* @__PURE__ */ jsx("span", { className: "text-gray-500 text-xs ml-1.5 text-[11px] whitespace-nowrap flex-shrink-0", children: item.type })
                ] }),
                item.groupName && /* @__PURE__ */ jsxs("span", { className: "text-gray-400 text-[11px] flex items-center mr-1 min-w-0 flex-shrink overflow-hidden", children: [
                  /* @__PURE__ */ jsx(
                    Icon,
                    {
                      name: "table",
                      className: "text-gray-400 mr-1 flex-shrink-0",
                      size: 12
                    }
                  ),
                  /* @__PURE__ */ jsx("span", { className: "truncate max-w-[80px]", children: item.groupName })
                ] })
              ]
            },
            item.ref
          );
        })
      }
    ),
    actionCardContainer
  );
}
function useMentionsTrigger({
  mentionsContextRef,
  isDisabled = false
}) {
  const triggerMentions = React.useCallback(() => {
    if (isDisabled) return;
    const context = mentionsContextRef.current;
    if (!(context == null ? void 0 : context.inputElement)) return;
    context.inputElement.focus();
    if (context.opened) {
      if (context.closeAutocomplete) {
        context.closeAutocomplete();
      }
      return;
    }
    try {
      const selection = window.getSelection();
      if (!selection) return;
      const configs = context.configs;
      if (!configs || configs.length === 0) return;
      const config = configs.find((cfg) => "@".match(cfg.query));
      if (!config) return;
      const { anchorNode } = selection;
      if (!anchorNode) return;
      const fragment = document.createElement("span");
      fragment.setAttribute("data-rich-mentions", "");
      fragment.setAttribute("spellcheck", "false");
      fragment.textContent = "@";
      if (config.customizeFragment) {
        config.customizeFragment(fragment, false);
      }
      const afterNode = document.createTextNode(" ");
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        if (!range.collapsed) {
          range.deleteContents();
        }
        range.insertNode(fragment);
        if (fragment.parentNode) {
          fragment.parentNode.insertBefore(afterNode, fragment.nextSibling);
        }
        const newRange = document.createRange();
        newRange.setStart(fragment.firstChild || fragment, 1);
        newRange.setEnd(fragment.firstChild || fragment, 1);
        selection.removeAllRanges();
        selection.addRange(newRange);
        if (context.openAutocomplete) {
          context.openAutocomplete(fragment, "@", config);
        }
        const inputEvent = new Event("input", {
          bubbles: true,
          cancelable: true
        });
        context.inputElement.dispatchEvent(inputEvent);
        context.inputElement.focus();
      }
    } catch (error) {
      console.warn("Failed to trigger mentions:", error);
      if (context.inputElement) {
        context.inputElement.focus();
      }
    }
  }, [mentionsContextRef, isDisabled]);
  return {
    triggerMentions
  };
}
const ActionCardWithTextArea = ({
  actions,
  isLoading,
  loadingText = "Applying transformation",
  textAreaValue = "",
  textAreaPlaceholder = 'Type a fix, like "remove leading zeros" or "standardize dates". Use @ to mention fields.',
  onTextAreaChange,
  onTextAreaKeyDown,
  onTextAreaPaste,
  textAreaDisabled = false,
  icon,
  mentions = [],
  hasMentions = false
}) => {
  var _a;
  const mentionsContextRef = React.useRef(null);
  const inputElementRef = useRef(null);
  const lastValueRef = useRef(textAreaValue);
  const isUserInputRef = useRef(false);
  const initialValueRef = useRef(textAreaValue);
  const [componentKey, setComponentKey] = React.useState(0);
  const isDisabled = React.useMemo(
    () => textAreaDisabled || isLoading,
    [textAreaDisabled, isLoading]
  );
  const { triggerMentions } = useMentionsTrigger({
    mentionsContextRef,
    isDisabled
  });
  const configs = React.useMemo(() => {
    if (mentions.length === 0) return [];
    return getConfigs(mentions);
  }, [mentions]);
  const focusInput = React.useCallback(() => {
    var _a2;
    const textarea = (_a2 = mentionsContextRef.current) == null ? void 0 : _a2.inputElement;
    if (textarea && !isDisabled && document.activeElement !== textarea) {
      try {
        textarea.focus();
        const range = document.createRange();
        range.selectNodeContents(textarea);
        range.collapse(false);
        const selection = window.getSelection();
        if (selection) {
          selection.removeAllRanges();
          selection.addRange(range);
        }
      } catch (error) {
        console.warn("Focus failed:", error);
      }
    }
  }, [isDisabled]);
  React.useEffect(() => {
    if (isDisabled) return;
    const focusTimeout = setTimeout(focusInput, 600);
    return () => clearTimeout(focusTimeout);
  }, [isDisabled, focusInput]);
  React.useEffect(() => {
    var _a2;
    if (isDisabled || !((_a2 = mentionsContextRef.current) == null ? void 0 : _a2.inputElement)) return;
    const readyTimeout = setTimeout(focusInput, 100);
    return () => clearTimeout(readyTimeout);
  }, [(_a = mentionsContextRef.current) == null ? void 0 : _a.inputElement, isDisabled, focusInput]);
  React.useEffect(() => {
    var _a2;
    const textarea = (_a2 = mentionsContextRef.current) == null ? void 0 : _a2.inputElement;
    if (textarea && isDisabled && document.activeElement === textarea) {
      textarea.blur();
    }
  }, [isDisabled]);
  React.useEffect(() => {
    const isDrasticChange = textAreaValue === "" && lastValueRef.current !== "" || // Clearing content
    textAreaValue !== "" && lastValueRef.current === "" || // Setting initial content
    Math.abs(textAreaValue.length - lastValueRef.current.length) > 20;
    if (isDrasticChange && !isUserInputRef.current) {
      initialValueRef.current = textAreaValue;
      setComponentKey((prev) => prev + 1);
      lastValueRef.current = textAreaValue;
    } else if (inputElementRef.current && textAreaValue !== lastValueRef.current && !isUserInputRef.current) {
      const context = mentionsContextRef.current;
      if ((context == null ? void 0 : context.getInitialHTML) && typeof context.getInitialHTML === "function") {
        const htmlContent = context.getInitialHTML(textAreaValue);
        inputElementRef.current.innerHTML = htmlContent;
      } else {
        inputElementRef.current.innerHTML = textAreaValue;
      }
      lastValueRef.current = textAreaValue;
    }
    isUserInputRef.current = false;
  }, [textAreaValue]);
  React.useEffect(() => {
    var _a2;
    const textarea = (_a2 = mentionsContextRef.current) == null ? void 0 : _a2.inputElement;
    if (!textarea) return;
    const handlePaste = (e) => {
      e.stopPropagation();
      setTimeout(() => {
        var _a3;
        if ((_a3 = mentionsContextRef.current) == null ? void 0 : _a3.getTransformedValue) {
          const transformedValue = mentionsContextRef.current.getTransformedValue();
          onTextAreaChange == null ? void 0 : onTextAreaChange(transformedValue);
        }
      }, 0);
    };
    textarea.addEventListener("paste", handlePaste, { capture: true });
    return () => {
      textarea.removeEventListener("paste", handlePaste, { capture: true });
    };
  }, [onTextAreaChange]);
  const handleRichMentionsPaste = (e) => {
    e.stopPropagation();
    const syntheticEvent = e;
    onTextAreaPaste == null ? void 0 : onTextAreaPaste(syntheticEvent);
  };
  const handleInputChange = React.useCallback(
    (event) => {
      if (!event.currentTarget) return;
      isUserInputRef.current = true;
      const rawHtml = event.currentTarget.innerHTML;
      let finalValue = "";
      if (mentionsContextRef.current && typeof mentionsContextRef.current.getTransformedValue === "function") {
        finalValue = processMentionsInHTML(rawHtml);
      } else {
        finalValue = event.currentTarget.innerText;
      }
      const element = document.createElement("div");
      element.innerHTML = rawHtml;
      const isEffectivelyEmpty = element.innerText.trim() === "";
      if (isEffectivelyEmpty) {
        finalValue = "";
        if (event.currentTarget.innerHTML !== "") {
          event.currentTarget.innerHTML = "";
        }
      }
      lastValueRef.current = finalValue;
      onTextAreaChange == null ? void 0 : onTextAreaChange(finalValue);
    },
    [onTextAreaChange]
  );
  const handleKeyDown = React.useCallback(
    (e) => {
      var _a2, _b, _c;
      if (isDisabled) {
        e.preventDefault();
        return;
      }
      if (e.key === "Backspace") {
        const selection = window.getSelection();
        if (!selection || !selection.rangeCount) return;
        const range = selection.getRangeAt(0);
        const { startContainer, startOffset } = range;
        let mentionSpan = null;
        if (startContainer.nodeType === Node.TEXT_NODE) {
          const parent = startContainer.parentElement;
          if (parent == null ? void 0 : parent.hasAttribute("data-rich-mentions")) {
            mentionSpan = parent;
          }
        } else if (startContainer.nodeType === Node.ELEMENT_NODE) {
          const element = startContainer;
          if (element.hasAttribute("data-rich-mentions")) {
            mentionSpan = element;
          }
        }
        if (!mentionSpan && startContainer.nodeType === Node.TEXT_NODE && startOffset === 0) {
          const prevSibling = startContainer.previousSibling;
          if ((prevSibling == null ? void 0 : prevSibling.nodeType) === Node.ELEMENT_NODE) {
            const prevElement = prevSibling;
            if (prevElement.hasAttribute("data-rich-mentions")) {
              mentionSpan = prevElement;
            }
          }
        }
        if (!mentionSpan && ((_a2 = mentionsContextRef.current) == null ? void 0 : _a2.inputElement)) {
          const cursorElement = range.startContainer;
          const inputElement = mentionsContextRef.current.inputElement;
          if (cursorElement === inputElement && startOffset === 0 && inputElement.lastElementChild) {
            const lastChild = inputElement.lastElementChild;
            if (lastChild.hasAttribute("data-rich-mentions")) {
              mentionSpan = lastChild;
            }
          }
        }
        if (mentionSpan) {
          e.preventDefault();
          mentionSpan.remove();
          const inputEvent = new Event("input", {
            bubbles: true,
            cancelable: true
          });
          if ((_b = mentionsContextRef.current) == null ? void 0 : _b.inputElement) {
            mentionsContextRef.current.inputElement.dispatchEvent(inputEvent);
          }
          return;
        }
      }
      if (e.key === "Enter") {
        const context = mentionsContextRef.current;
        if ((context == null ? void 0 : context.opened) && ((_c = context == null ? void 0 : context.results) == null ? void 0 : _c.length) > 0) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
      }
      const syntheticEvent = e;
      onTextAreaKeyDown == null ? void 0 : onTextAreaKeyDown(syntheticEvent);
    },
    [isDisabled, onTextAreaKeyDown]
  );
  const renderInput = () => {
    return /* @__PURE__ */ jsxs(
      RichMentionsProvider,
      {
        configs: hasMentions ? configs : [],
        getContext: (context) => {
          mentionsContextRef.current = context;
          if (context == null ? void 0 : context.setInputElement) {
            const originalSetInputElement = context.setInputElement;
            context.setInputElement = (element) => {
              inputElementRef.current = element;
              originalSetInputElement(element);
            };
          }
        },
        children: [
          /* @__PURE__ */ jsxs("div", { className: "relative", children: [
            /* @__PURE__ */ jsx("style", { children: `
              .custom-placeholder:empty::before {
                content: attr(data-placeholder);
                color: rgba(131, 131, 131, 1) !important;
                font-size: 0.82rem !important;
                pointer-events: none;
                line-height: 1.5;
                display: block;
                min-height: 1.5rem;
              }
              .custom-placeholder {
                min-height: 4.5rem !important;
                height: auto !important;
                line-height: 1.5 !important;
              }
            ` }),
            /* @__PURE__ */ jsx(
              RichMentionsInput,
              {
                defaultValue: initialValueRef.current,
                autoFocus: !isDisabled,
                className: `custom-placeholder flex-1 text-primary font-medium resize-none text-md placeholder:text-md placeholder:text-muted-foreground focus:outline-none focus:ring-0 shadow-none overflow-y-auto overflow-x-hidden whitespace-pre-wrap break-words ${isDisabled ? "opacity-50 cursor-not-allowed pointer-events-none bg-gray-50" : ""}`,
                "data-placeholder": textAreaPlaceholder,
                placeholder: textAreaPlaceholder,
                disabled: isDisabled,
                contentEditable: !isDisabled,
                onInput: handleInputChange,
                onKeyDown: handleKeyDown,
                onPaste: (e) => {
                  if (isDisabled) {
                    e.preventDefault();
                    return;
                  }
                  handleRichMentionsPaste(e);
                },
                style: {
                  outline: "none",
                  boxShadow: "none",
                  minHeight: "4.5rem",
                  height: "auto",
                  maxHeight: "12.5rem",
                  overflowWrap: "break-word",
                  wordBreak: "break-word",
                  lineHeight: "1.5"
                }
              }
            )
          ] }),
          hasMentions && configs.length > 0 && !isDisabled && /* @__PURE__ */ jsx(MentionsPopover, {})
        ]
      },
      `${componentKey}-${mentions.map((m) => m.ref).join("-")}`
    );
  };
  return /* @__PURE__ */ jsxs("div", { className: "w-full bg-background-90 text-gray-900 font-medium text-md p-2 rounded-sm flex flex-col gap-0.5 min-h-[6rem]", children: [
    /* @__PURE__ */ jsxs("div", { className: "text-muted-foreground text-md font-medium flex items-center gap-1 flex-1 min-h-[3rem]", children: [
      icon && /* @__PURE__ */ jsx(
        Icon,
        {
          name: icon,
          size: 16,
          className: "text-muted-foreground flex-shrink-0"
        }
      ),
      renderInput()
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1 w-full pt-1 pb-0 pl-1 pr-1 h-[2.5rem] flex-shrink-0 relative", children: [
      hasMentions && !isDisabled && /* @__PURE__ */ jsx(
        Button,
        {
          variant: "ghost",
          size: "sm",
          className: "flex items-center justify-center p-0 hover:bg-gray-50 absolute left-1 top-1/2 transform -translate-y-1/2 z-10",
          onClick: triggerMentions,
          title: "Insert mention",
          children: /* @__PURE__ */ jsx(
            Icon,
            {
              name: "at",
              size: 16,
              className: "!text-muted-foreground hover:!text-gray-600",
              style: { color: "currentColor" }
            }
          )
        }
      ),
      (() => {
        const primaryActions = actions.filter(
          (action) => action.variant === "primary"
        );
        const otherActions = actions.filter(
          (action) => action.variant !== "primary"
        );
        const allActionsToRender = [...otherActions, ...primaryActions];
        const renderButton = (action, isSingleAction = false) => /* @__PURE__ */ jsx(
          Button,
          {
            variant: action.variant,
            size: "sm",
            onClick: action.onClick,
            disabled: action.disabled || action.loading || isLoading,
            icon: !(action.loading || isLoading) ? action.icon : void 0,
            className: `
                ${isSingleAction ? "" : "w-full max-w-full"}
                min-w-[120px] text-md font-medium py-1.5 px-2 rounded flex items-center justify-center gap-1 
                transition-all duration-200 ease-out border-0
                ${action.variant === "primary" ? "bg-gray-800 hover:bg-gray-900 !text-white border-0" : action.variant === "outline" ? "bg-gray-100 hover:bg-gray-50 text-gray-700 border-0" : "bg-white hover:bg-gray-25 text-gray-700 border-0"}
              `,
            children: /* @__PURE__ */ jsxs("div", { className: "flex items-center", children: [
              (action.loading || isLoading) && /* @__PURE__ */ jsx(Icon, { name: "loader", size: 16, className: "animate-spin mr-1" }),
              /* @__PURE__ */ jsx("span", { children: action.loading || isLoading ? loadingText : action.label })
            ] })
          }
        );
        if (allActionsToRender.length === 1) {
          const action = allActionsToRender[0];
          return /* @__PURE__ */ jsx("div", { className: "flex justify-end w-full", children: /* @__PURE__ */ jsx("div", { className: "", children: /* @__PURE__ */ jsxs(
            Button,
            {
              variant: action.variant,
              size: "sm",
              onClick: action.onClick,
              disabled: action.disabled || action.loading || isLoading,
              icon: !(action.loading || isLoading) ? action.icon : void 0,
              className: `
                      text-md font-medium px-2.5 py-1.5 rounded flex items-center justify-center gap-1 
                      transition-all duration-200 ease-out border-0
                      ${action.variant === "primary" ? "bg-gray-800 hover:bg-gray-900 !text-white" : action.variant === "outline" ? "border border-table-border bg-gray-100 hover:bg-gray-50 text-gray-700 border-0" : "bg-white hover:bg-gray-25 text-gray-700 border-0"}
                    `,
              children: [
                action.loading && /* @__PURE__ */ jsx("div", { className: "w-3 h-3 border border-current border-t-transparent rounded-full animate-spin mr-1" }),
                /* @__PURE__ */ jsx("span", { children: action.loading || isLoading ? loadingText : action.label })
              ]
            }
          ) }) });
        }
        return allActionsToRender.map((action, index) => /* @__PURE__ */ jsx("div", { className: "flex-1 p-1", children: renderButton(action, false) }, `${action.label}-${index}`));
      })()
    ] })
  ] });
};
export {
  ActionCard,
  ActionCardWithTextArea,
  AreaChartComponent as AreaChart,
  AssistantMessagePrimitive,
  BarChartComponent as BarChart,
  ChatEmptyState,
  ChatHeader,
  ChatInput,
  ChatInputPrimitive,
  ChatMessage,
  ChatMessagePrimitive,
  ChatStatus,
  ChatStatusPrimitive,
  ChatUI,
  ErrorMessagePrimitive,
  FileAttachmentPrimitive,
  MODELS,
  OperationList,
  PieChartComponent as PieChart,
  ReasoningPrimitive,
  SnapshotPrimitive,
  StatusMessage,
  ToolCallPrimitive,
  UserMessagePrimitive
};
